import React, { useContext, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import ExistingApiRegistrationDialog from "./ExistingApiRegistrationDialog";
import CustomerSoftwareDialog from "./CustomerSoftwareDialog";
import CompanyLogoDialog from "./CompanyLogoDialog";
import html2pdf from "html2pdf.js";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { pdfjs } from "react-pdf";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Switch
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import ReplayIcon from "@material-ui/icons/Replay";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DescriptionIcon from "@material-ui/icons/Description";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import CustomerPrefixesTable from "./CustomerPrefixesTable";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(3),
    borderRadius: 24,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(14,27,40,0.98) 0%, rgba(21,75,117,0.96) 100%)"
        : "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(215,234,250,1) 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(28,78,125,0.08)",
    boxShadow: "0 24px 60px rgba(16, 63, 104, 0.14)",
    marginBottom: theme.spacing(3)
  },
  metricCard: {
    borderRadius: 22,
    padding: theme.spacing(2.3),
    minHeight: 120,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(160deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
        : "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(246,250,255,0.92) 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  filterCard: {
    borderRadius: 24,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  tableCard: {
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  tableWrap: {
    overflowX: "auto"
  },
  tableHead: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "linear-gradient(180deg, rgba(245,249,253,1) 0%, rgba(235,243,250,1) 100%)"
  },
  th: {
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 700,
    whiteSpace: "nowrap"
  },
  row: {
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.025)" : "rgba(28,78,125,0.03)"
    }
  },
  idCell: {
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  nameBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 220
  },
  actionButton: {
    minWidth: 36
  },
  detailSection: {
    borderRadius: 18,
    padding: theme.spacing(2),
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.02)"
      : "rgba(247,250,253,0.9)"
  },
  detailMetric: {
    borderRadius: 16,
    padding: theme.spacing(1.75),
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.03)"
      : "#fff"
  },
  compactList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.2)
  },
  compactRow: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.025)"
      : "#fff"
  }
}));

const emptyClientForm = {
  id: null,
  name: "",
  identificationNumber: "",
  installationCode: "",
  dv: "",
  email: "",
  phone: "",
  address: "",
  stateName: "",
  merchantRegistration: "",
  softwareClassificationCode: "",
  commercialClassificationCode: "",
  catalogSelections: {
    tax: null,
    environment: null,
    payroll_environment: null,
    eqdocs_environment: null,
    operation: null,
    liability: null
  },
  primaryNotificationPhone: "",
  primaryNotificationEmail: "",
  notes: "",
  isActive: true
};

const emptyEstablishmentForm = {
  id: null,
  installationCode: "",
  name: "",
  address: "",
  departmentCode: "",
  departmentName: "",
  municipalityCode: "",
  municipalityName: "",
  primaryPhone: "",
  primaryEmail: "",
  notes: "",
  isActive: true
};

const emptyRutOnboardingForm = {
  sourceId: "",
  rutFile: null,
  resolutionFiles: [],
  certificateFile: null,
  certificatePassword: "",
  logoFile: null,
  invoice: true,
  payroll: false,
  eqdocs: false,
  softwareClassificationCode: "",
  commercialClassificationCode: "",
  setupEnvironment: true,
  setupTestResolutions: true,
  softwareName: "",
  softwareId: "",
  softwarePin: "12345",
  runInvoiceTests: false,
  runPayrollTests: false,
  invoiceTestSetId: "",
  payrollTestSetId: "",
  invoiceTestStartNumber: "",
  creditNoteTestNumber: "",
  debitNoteTestNumber: "",
  completeExisting: false,
  updateExisting: false,
  overridesJson: "",
  resolutionsJson: "",
  showAdvanced: false,
  confirmCreate: false
};

const emptyApiEnvironmentForm = {
  invoice_environment: "",
  payroll_environment: "",
  eqdocs_environment: ""
};

const emptyApiPlanForm = {
  service: "invoice",
  plan_id: "",
  absolut_plan_documents: "",
  renew: true,
  notes: ""
};

const emptyApiPrefixForm = {
  id: null,
  resolution: "",
  type_document_id: "",
  prefix: "",
  from: "1",
  to: "99999999",
  date_from: "",
  date_to: ""
};

const ABSOLUTE_PLAN_SERVICE = "absolute";
const API_PREFIX_ALLOWED_TYPE_IDS = [4, 5, 9, 10, 11, 13, 12, 15, 25, 26];
const API_PREFIX_TYPE_COLORS = [
  { background: "#D7F0FF", color: "#075985", border: "#7DD3FC" },
  { background: "#E8F7D2", color: "#3F6212", border: "#BEF264" },
  { background: "#FFF1C2", color: "#854D0E", border: "#FDE68A" },
  { background: "#FFE4E6", color: "#9F1239", border: "#FDA4AF" },
  { background: "#EDE9FE", color: "#5B21B6", border: "#C4B5FD" },
  { background: "#DCFCE7", color: "#166534", border: "#86EFAC" },
  { background: "#FCE7F3", color: "#9D174D", border: "#F9A8D4" },
  { background: "#CCFBF1", color: "#0F766E", border: "#5EEAD4" },
  { background: "#E0E7FF", color: "#3730A3", border: "#A5B4FC" },
  { background: "#FEF3C7", color: "#92400E", border: "#FCD34D" }
];

const getApiPrefixTypeColor = typeDocumentId => {
  const numericId = Number(typeDocumentId || 0);
  const index = API_PREFIX_ALLOWED_TYPE_IDS.indexOf(numericId);
  return API_PREFIX_TYPE_COLORS[index >= 0 ? index % API_PREFIX_TYPE_COLORS.length : numericId % API_PREFIX_TYPE_COLORS.length];
};

const toPositiveNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizePlanOption = (plan, fallbackService = "invoice", index = 0) => {
  if (!plan || typeof plan !== "object") {
    return null;
  }

  const planId = toPositiveNumber(
    plan.plan_id ??
    plan.planId ??
    plan.id ??
    plan.plan?.id ??
    plan.plan?.plan_id
  );

  if (!planId) {
    return null;
  }

  const service = String(
    plan.service ||
    plan.planType ||
    plan.type ||
    plan.category ||
    fallbackService
  ).trim() || fallbackService;

  const name = String(
    plan.planName ||
    plan.plan_name ||
    plan.name ||
    plan.description ||
    plan.title ||
    `Plan ${planId}`
  ).trim() || `Plan ${planId}`;

  const documentsValue = plan.documents ?? plan.folios ?? plan.quantity ?? plan.document_count;
  const documents = documentsValue !== undefined && documentsValue !== null && String(documentsValue).trim() !== ""
    ? Number(documentsValue)
    : null;

  const startsAt = plan.startDate || plan.start_date || plan.starts_at || plan.created_at || null;
  const suffix = [
    documents && Number.isFinite(documents) ? `${documents} folios` : null,
    startsAt ? `inicio ${String(startsAt).slice(0, 10)}` : null
  ].filter(Boolean).join(" - ");

  return {
    id: planId,
    service,
    label: suffix ? `${name} - ${suffix}` : name,
    raw: plan,
    sortKey: `${service}-${String(name).toLowerCase()}-${String(planId)}-${index}`
  };
};

const extractPlanOptions = rawPlans => {
  const payload = rawPlans?.result || rawPlans || {};
  const candidates = [];
  const addEntries = (entries, fallbackService) => {
    if (!Array.isArray(entries)) return;
    entries.forEach((entry, index) => {
      const option = normalizePlanOption(entry, fallbackService, index);
      if (option) {
        candidates.push(option);
      }
    });
  };

  addEntries(Array.isArray(payload?.plans) ? payload.plans : null, "invoice");
  addEntries(Array.isArray(payload?.current_plans) ? payload.current_plans : null, "invoice");
  addEntries(Array.isArray(payload?.type_plans) ? payload.type_plans : null, "invoice");
  addEntries(Array.isArray(payload?.available_plans) ? payload.available_plans : null, "invoice");
  addEntries(Array.isArray(payload?.plan_types) ? payload.plan_types : null, "invoice");

  if (payload?.plans && !Array.isArray(payload.plans) && typeof payload.plans === "object") {
    Object.entries(payload.plans).forEach(([service, entries]) => addEntries(entries, service));
  }

  if (payload?.type_plans && !Array.isArray(payload.type_plans) && typeof payload.type_plans === "object") {
    Object.entries(payload.type_plans).forEach(([service, entries]) => addEntries(entries, service));
  }

  const unique = new Map();
  candidates.forEach(option => {
    const key = `${option.service}-${option.id}`;
    if (!unique.has(key)) {
      unique.set(key, option);
    }
  });

  return Array.from(unique.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey, "es"));
};

const RUT_ONBOARDING_DRAFT_KEY = "soluwhats.rut-onboarding-draft.v1";

const readRutOnboardingDraft = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RUT_ONBOARDING_DRAFT_KEY) || "null");
    return stored && typeof stored === "object" ? stored : null;
  } catch (_error) {
    return null;
  }
};

const persistRutOnboardingDraft = form => {
  const { rutFile, certificateFile, logoFile, resolutionFiles, ...draft } = form;
  window.localStorage.setItem(RUT_ONBOARDING_DRAFT_KEY, JSON.stringify({ ...draft, savedAt: new Date().toISOString() }));
};

const clearRutOnboardingDraft = () => window.localStorage.removeItem(RUT_ONBOARDING_DRAFT_KEY);

const RutHelpLabel = ({ label, help }) => (
  <Box component="span" display="inline-flex" alignItems="center" gridGap={3}>
    {label}
    <Tooltip title={help} arrow placement="top">
      <HelpOutlineIcon style={{ fontSize: 16, cursor: "help", opacity: 0.75 }} />
    </Tooltip>
  </Box>
);

// Uses the same worker source already used for message PDF previews.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const fileToBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",").pop() || "");
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const RUT_MISSING_FIELD_LABELS = {
  identification_number: "NIT o cedula",
  dv: "digito de verificacion",
  business_name: "nombre o razon social",
  address: "direccion",
  email: "correo",
  phone: "telefono",
  municipality_id: "municipio"
};

const getRutResultPayload = result => result?.result || result?.data || result || {};

const getRutMissingFields = result => {
  const payload = getRutResultPayload(result);
  const fields = payload?.missing || result?.missing || result?.errors;
  if (Array.isArray(fields)) return fields;
  if (fields && typeof fields === "object") return Object.keys(fields);
  return [];
};

const formatRutMissingFields = result => getRutMissingFields(result)
  .map(field => RUT_MISSING_FIELD_LABELS[field] || field)
  .join(", ");

const inspectResolutionPdf = async file => {
  if (!/\.pdf$/i.test(file.name) || file.size > 8 * 1024 * 1024) {
    throw new Error("Cada resolucion debe ser un PDF de maximo 8 MB.");
  }

  const binary = await file.arrayBuffer();
  const signature = new TextDecoder("latin1").decode(binary.slice(0, 5));
  if (signature !== "%PDF-") {
    throw new Error(`${file.name} no contiene un PDF valido.`);
  }

  let documentProxy;
  try {
    documentProxy = await pdfjs.getDocument({ data: binary.slice(0) }).promise;
    const maxPages = Math.min(documentProxy.numPages, 3);
    let text = "";
    for (let pageNumber = 1; pageNumber <= maxPages; pageNumber += 1) {
      const page = await documentProxy.getPage(pageNumber);
      const content = await page.getTextContent();
      text += content.items.map(item => item.str || "").join(" ");
    }
    const normalizedText = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    if (normalizedText.trim().length < 30) {
      throw new Error(`${file.name} parece una imagen o escaneo sin texto legible.`);
    }
    const hasDianIssuer = /\bDIAN\b|DIRECCION\s+DE\s+IMPUESTOS|U\.?A\.?(E)?\.?/.test(normalizedText);
    const resolutionMarkers = [
      "RESOLUCION",
      "RANGOS DE NUMERACION",
      "FACTURA ELECTRONICA",
      "HABILITACION",
      "PREFIJO",
      "DESDE EL NUMERO",
      "HASTA EL NUMERO"
    ].filter(marker => normalizedText.includes(marker)).length;

    // DIAN has more than one official PDF layout. Some resolutions are headed
    // by "Rangos de numeracion" and "Habilitacion" rather than "Resolucion".
    if (!hasDianIssuer || resolutionMarkers < 2) {
      throw new Error(`${file.name} no parece una resolucion DIAN legible.`);
    }
    return { file, pages: documentProxy.numPages };
  } catch (error) {
    if (error?.name === "PasswordException") {
      throw new Error(`${file.name} esta protegido con clave y no puede procesarse.`);
    }
    throw error;
  } finally {
    documentProxy?.destroy?.();
  }
};

const metricStyle = active => ({
  backgroundColor: active ? "rgba(29,154,108,0.14)" : "rgba(214,69,69,0.14)",
  color: active ? "#1D9A6C" : "#D64545",
  fontWeight: 700
});

const formatDisplayDate = value => {
  if (!value) {
    return "sin fecha";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString("es-CO");
};

const normalizeCatalogLabel = value => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .trim()
  .toUpperCase();

const canSyncResolutionToNube = customer => {
  const classification = normalizeCatalogLabel(customer?.softwareClassificationCode);
  return classification === "FACILWEB NUBE" || classification === "FACILWEB NUBE + NOMINA";
};

const catalogValue = (customer, catalogType) => {
  const values = (customer?.catalogValues || []).filter(item => String(item.catalogType || "") === catalogType);
  if (!values.length) return null;

  const currentSourceId = customer?.integrationSourceId ? String(customer.integrationSourceId) : "";
  const sortedValues = [...values].sort((left, right) => {
    const leftCurrent = currentSourceId && String(left.integrationSourceId || "") === currentSourceId ? 1 : 0;
    const rightCurrent = currentSourceId && String(right.integrationSourceId || "") === currentSourceId ? 1 : 0;
    if (leftCurrent !== rightCurrent) return rightCurrent - leftCurrent;

    const leftDate = new Date(left.updatedAt || left.createdAt || 0).getTime() || 0;
    const rightDate = new Date(right.updatedAt || right.createdAt || 0).getTime() || 0;
    return rightDate - leftDate;
  });

  return sortedValues[0] || null;
};

const catalogEnvironmentValue = (customer, catalogType) => {
  const value = catalogValue(customer, catalogType);
  const code = String(value?.catalogCode || "").trim();
  const name = normalizeCatalogLabel(value?.catalogName);
  if (code === "1" || name.includes("PRODUCCION")) return "production";
  if (code === "2" || name.includes("HABILITACION")) return "habilitation";
  return "";
};

const environmentLabel = value => {
  if (value === "production") return "Produccion";
  if (value === "habilitation") return "Habilitacion";
  return "Sin dato";
};

const CLIENT_EDITABLE_CATALOG_LABELS = {
  tax: "Impuestos",
  operation: "Operacion",
  liability: "Responsable de IVA"
};

const CLIENT_CATALOG_LABELS = {
  ...CLIENT_EDITABLE_CATALOG_LABELS,
  environment: "Ambiente FE",
  payroll_environment: "Ambiente nomina",
  eqdocs_environment: "Ambiente documento soporte",
  language: "Idioma",
  document_identification: "Tipo documento",
  country: "Pais",
  currency: "Moneda",
  organization: "Organizacion",
  regime: "Regimen",
  department: "Departamento",
  municipality: "Municipio"
};

const PLAN_SERVICE_LABELS = {
  invoice: "Factura electronica",
  payroll: "Nomina",
  radian: "RADIAN",
  support_document: "Documento soporte",
  absolute: "Paquete de folios"
};

const getApiPlansPayload = rawPlans => rawPlans?.result || rawPlans || {};

const formatPlanNumber = value => {
  if (value === null || value === undefined || value === "") return "Sin dato";
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("es-CO") : String(value);
};

const formatPlanDate = value => {
  if (!value) return "Sin fecha";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("es-CO");
};

const buildCurrentPlanRows = rawPlans => {
  const payload = getApiPlansPayload(rawPlans);
  const sourcePlans = Array.isArray(payload.current_plans) ? payload.current_plans : [];
  if (sourcePlans.length) {
    return sourcePlans.map(plan => ({
      service: plan.service,
      serviceLabel: PLAN_SERVICE_LABELS[plan.service] || plan.service || "Servicio",
      planName: plan.plan_name || plan.name || "Sin plan",
      documents: plan.documents,
      usedDocuments: plan.used_documents,
      remainingDocuments: plan.remaining_documents,
      isExhausted: plan.is_exhausted,
      startDate: plan.start_date,
      calculationMode: plan.calculation_mode
    }));
  }

  if (payload.current && typeof payload.current === "object") {
    return Object.entries(payload.current).map(([service, plan]) => ({
      service,
      serviceLabel: PLAN_SERVICE_LABELS[service] || service,
      planName: plan?.plan_name || plan?.name || (service === "absolute" ? "Paquete de folios" : "Sin plan"),
      documents: plan?.documents ?? plan?.qty_docs,
      usedDocuments: plan?.used_documents,
      remainingDocuments: plan?.remaining_documents,
      isExhausted: plan?.is_exhausted,
      startDate: plan?.start_date,
      calculationMode: plan?.calculation_mode
    }));
  }

  return [];
};

const buildPlanHistoryRows = rawPlans => {
  const payload = getApiPlansPayload(rawPlans);
  const history = Array.isArray(payload.history) ? payload.history : [];
  const planOptions = extractPlanOptions(payload);
  const findPlan = (service, planId) => planOptions.find(option => (
    option.service === service && String(option.id) === String(planId || "")
  ));

  return history
    .map(item => {
      const previousPlan = findPlan(item.service, item.previous_plan_id);
      const newPlan = findPlan(item.service, item.new_plan_id);
      return {
        id: item.id,
        date: item.created_at || item.updated_at,
        service: item.service,
        serviceLabel: PLAN_SERVICE_LABELS[item.service] || item.service || "Servicio",
        action: item.action || "Movimiento",
        previousPlanLabel: item.service === ABSOLUTE_PLAN_SERVICE ? "Paquete" : (previousPlan?.raw?.name || previousPlan?.label || (item.previous_plan_id ? `Plan ${item.previous_plan_id}` : "Sin dato")),
        newPlanLabel: item.service === ABSOLUTE_PLAN_SERVICE ? "Paquete" : (newPlan?.raw?.name || newPlan?.label || (item.new_plan_id ? `Plan ${item.new_plan_id}` : "Sin dato")),
        previousDocuments: item.service === ABSOLUTE_PLAN_SERVICE ? item.previous_absolute_documents : previousPlan?.raw?.documents,
        newDocuments: item.service === ABSOLUTE_PLAN_SERVICE ? item.new_absolute_documents : newPlan?.raw?.documents,
        notes: item.notes || ""
      };
    })
    .sort((a, b) => {
      const dateDiff = new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
      if (dateDiff) return dateDiff;
      return Number(b.id || 0) - Number(a.id || 0);
    });
};

const hasUsableClientContactName = contact => {
  const name = String(contact?.name || "").trim();
  const number = String(contact?.number || "").trim();

  if (!name) return false;
  if (!/^\d+$/.test(name)) return true;
  if (!number) return false;

  return name !== number && name.length < 12;
};

const buildClientContactLabel = contact => {
  if (!contact) return "";

  const rawName = String(contact.name || "").trim();
  const rawNumber = String(contact.number || "").trim();
  const name = hasUsableClientContactName(contact) ? rawName : "";

  if (name && rawNumber) {
    return `${name} - ${rawNumber}`;
  }

  return name || rawNumber || `Contacto #${contact.id}`;
};

const Clients = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.super || user?.profile === "admin";

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [debouncedSearchParam, setDebouncedSearchParam] = useState("");
  const [softwareClassification, setSoftwareClassification] = useState("");
  const [commercialClassification, setCommercialClassification] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("DESC");
  const [catalogs, setCatalogs] = useState({
    software: [],
    commercial: [],
    attributes: {
      tax: [],
      environment: [],
      payroll_environment: [],
      eqdocs_environment: [],
      operation: [],
      liability: []
    }
  });
  const [geoCatalogs, setGeoCatalogs] = useState({ departments: [], municipalities: [] });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [count, setCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [clientStatusConfirmation, setClientStatusConfirmation] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [apiTokensOpen, setApiTokensOpen] = useState(false);
  const [apiTokensLoading, setApiTokensLoading] = useState(false);
  const [apiTokensCustomer, setApiTokensCustomer] = useState(null);
  const [apiTokensData, setApiTokensData] = useState(null);

  const [establishmentModalOpen, setEstablishmentModalOpen] = useState(false);
  const [establishmentSaving, setEstablishmentSaving] = useState(false);
  const [establishmentForm, setEstablishmentForm] = useState(emptyEstablishmentForm);
  const [contactLinkDialog, setContactLinkDialog] = useState(false);
  const [contactLinkSelection, setContactLinkSelection] = useState([]);
  const [contactLinkOptions, setContactLinkOptions] = useState([]);
  const [contactLinkLoading, setContactLinkLoading] = useState(false);
  const [existingRegistrationCustomer, setExistingRegistrationCustomer] = useState(null);
  const [softwareCustomer, setSoftwareCustomer] = useState(null);
  const [logoCustomer, setLogoCustomer] = useState(null);
  const [rutOnboardingOpen, setRutOnboardingOpen] = useState(false);
  const [rutSources, setRutSources] = useState([]);
  const [rutOnboardingForm, setRutOnboardingForm] = useState(emptyRutOnboardingForm);
  const [rutOnboardingSaving, setRutOnboardingSaving] = useState(false);
  const [rutOnboardingResult, setRutOnboardingResult] = useState(null);
  const [rutOnboardingHelpOpen, setRutOnboardingHelpOpen] = useState(false);
  const [rutOnboardingDraftRestored, setRutOnboardingDraftRestored] = useState(false);
  const [clientActionAnchor, setClientActionAnchor] = useState(null);
  const [clientActionRecord, setClientActionRecord] = useState(null);
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [resolutionCustomer, setResolutionCustomer] = useState(null);
  const [resolutionHistory, setResolutionHistory] = useState([]);
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [resolutionProcessing, setResolutionProcessing] = useState(false);
  const [resolutionFile, setResolutionFile] = useState(null);
  const [syncResolutionToNube, setSyncResolutionToNube] = useState(false);
  const [nubeSyncConfirmation, setNubeSyncConfirmation] = useState(null);
  const [nubeSyncApplying, setNubeSyncApplying] = useState(false);
  const [resolutionHelpOpen, setResolutionHelpOpen] = useState(false);
  const [resolutionDetailOpen, setResolutionDetailOpen] = useState(false);
  const [resolutionDetail, setResolutionDetail] = useState(null);
  const [apiOperationsOpen, setApiOperationsOpen] = useState(false);
  const [apiOperationsCustomer, setApiOperationsCustomer] = useState(null);
  const [apiOperationsStatus, setApiOperationsStatus] = useState(null);
  const [apiOperationsPlans, setApiOperationsPlans] = useState(null);
  const [apiOperationsLoading, setApiOperationsLoading] = useState(false);
  const [apiOperationsSaving, setApiOperationsSaving] = useState(false);
  const [apiOperationsTab, setApiOperationsTab] = useState(0);
  const [apiEnvironmentForm, setApiEnvironmentForm] = useState(emptyApiEnvironmentForm);
  const [apiPlanForm, setApiPlanForm] = useState(emptyApiPlanForm);
  const [apiEnvironmentConfirmation, setApiEnvironmentConfirmation] = useState(null);
  const [apiPlanConfirmation, setApiPlanConfirmation] = useState(null);
  const [apiPrefixesOpen, setApiPrefixesOpen] = useState(false);
  const [apiPrefixesCustomer, setApiPrefixesCustomer] = useState(null);
  const [apiPrefixesStatus, setApiPrefixesStatus] = useState(null);
  const [apiPrefixesData, setApiPrefixesData] = useState(null);
  const [resolutionAssignments, setResolutionAssignments] = useState({ links: [], establishments: [] });
  const [resolutionAssignmentSaving, setResolutionAssignmentSaving] = useState(false);
  const [resolutionCopy, setResolutionCopy] = useState(null);
  const [apiPrefixesSourceId, setApiPrefixesSourceId] = useState("");
  const [apiPrefixesLoading, setApiPrefixesLoading] = useState(false);
  const [apiPrefixForm, setApiPrefixForm] = useState(emptyApiPrefixForm);
  const [apiPrefixEditorOpen, setApiPrefixEditorOpen] = useState(false);
  const [apiPrefixSaving, setApiPrefixSaving] = useState(false);
  const [apiPrefixConfirmation, setApiPrefixConfirmation] = useState(null);
  const [syncAllApiLoading, setSyncAllApiLoading] = useState(false);

  const activeCount = useMemo(() => records.filter(item => item.isActive).length, [records]);
  const manualCount = useMemo(
    () => records.filter(item => String(item.originCode || "").toLowerCase() === "manual").length,
    [records]
  );
  const isAbsolutePlanService = apiPlanForm.service === ABSOLUTE_PLAN_SERVICE;
  const apiAllSelectablePlans = useMemo(
    () => extractPlanOptions(apiOperationsPlans).filter(option => option.service !== ABSOLUTE_PLAN_SERVICE),
    [apiOperationsPlans]
  );
  const apiSelectablePlans = useMemo(
    () => apiAllSelectablePlans.filter(option => option.service === apiPlanForm.service),
    [apiAllSelectablePlans, apiPlanForm.service]
  );
  const apiSelectedPlanOption = useMemo(
    () => apiSelectablePlans.find(option => String(option.id) === String(apiPlanForm.plan_id || "")) || null,
    [apiSelectablePlans, apiPlanForm.plan_id]
  );
  const apiCurrentPlanRows = useMemo(
    () => buildCurrentPlanRows(apiOperationsPlans),
    [apiOperationsPlans]
  );
  const apiPlanHistoryRows = useMemo(
    () => buildPlanHistoryRows(apiOperationsPlans),
    [apiOperationsPlans]
  );
  const apiPrefixRows = useMemo(
    () => Array.isArray(apiPrefixesData?.result?.prefixes) ? apiPrefixesData.result.prefixes : [],
    [apiPrefixesData]
  );
  const apiPrefixTypeOptions = useMemo(() => {
    const options = new Map();
    const addOption = option => {
      const id = option?.id ?? option?.type_document_id;
      if (!id) return;
      if (!API_PREFIX_ALLOWED_TYPE_IDS.includes(Number(id))) return;
      const code = option?.code || option?.type_document_code || "";
      const name = option?.name || option?.type_document_name || "";
      options.set(String(id), {
        id,
        code,
        name,
        label: [code, name].filter(Boolean).join(" - ") || `Tipo ${id}`
      });
    };

    (apiPrefixesData?.result?.type_documents || []).forEach(addOption);
    apiPrefixRows.forEach(addOption);
    return Array.from(options.values()).sort((a, b) => {
      const orderA = API_PREFIX_ALLOWED_TYPE_IDS.indexOf(Number(a.id));
      const orderB = API_PREFIX_ALLOWED_TYPE_IDS.indexOf(Number(b.id));
      return orderA - orderB;
    });
  }, [apiPrefixesData, apiPrefixRows]);

  const totalPages = useMemo(() => {
    if (pageSize === -1) {
      return 1;
    }
    return Math.max(Math.ceil(count / pageSize), 1);
  }, [count, pageSize]);

  const loadCatalogs = async () => {
    try {
      const [{ data }, { data: geoData }] = await Promise.all([
        api.get("/clients/catalogs"),
        api.get("/clients/geo/catalogs")
      ]);
      setCatalogs({
        software: Array.isArray(data?.software) ? data.software : [],
        commercial: Array.isArray(data?.commercial) ? data.commercial : [],
        attributes: data?.attributes || {
          tax: [],
          environment: [],
          payroll_environment: [],
          eqdocs_environment: [],
          operation: [],
          liability: []
        }
      });
      setGeoCatalogs({
        departments: Array.isArray(geoData?.departments) ? geoData.departments : [],
        municipalities: Array.isArray(geoData?.municipalities) ? geoData.municipalities : []
      });
    } catch (error) {
      if (error?.response?.status !== 403) {
        toastError(error);
      }
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/clients", {
        params: {
          searchParam: debouncedSearchParam,
          softwareClassification,
          commercialClassification,
          activeFilter,
          sortBy,
          sortDirection,
          pageNumber,
          pageSize: pageSize === -1 ? "all" : pageSize
        }
      });

      setRecords(Array.isArray(data?.records) ? data.records : []);
      setCount(Number(data?.count || 0));
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = rows => {
    const exportRows = rows.map(item => ({
      ID: item.id,
      Cliente: item.name || "",
      NIT_Cedula: `${item.identificationNumber || ""}${item.dv ? `-${item.dv}` : ""}`,
      Correo: item.email || "",
      Telefono: item.phone || item.primaryNotificationPhone || "",
      Direccion: item.address || "",
      Estado: item.stateName || "",
      Software: item.softwareClassificationCode || "",
      ClasificacionComercial: item.commercialClassificationCode || "",
      Activo: item.isActive ? "Si" : "No",
      Origen: item.originCode || "",
      Fuente: item.integrationSource?.name || "",
      Actualizado: item.updatedAt ? new Date(item.updatedAt).toLocaleString("es-CO") : ""
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, `clientes-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = async rows => {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = `
      <div style="padding:24px;font-family:Arial,sans-serif;color:#1f2937;">
        <h1 style="margin:0 0 8px;">Listado de clientes</h1>
        <p style="margin:0 0 18px;">Generado: ${new Date().toLocaleString("es-CO")}</p>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead>
            <tr>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Cliente</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Documento</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Software</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Clasificacion</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Correo</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Telefono</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(item => `
              <tr>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.name || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.identificationNumber || ""}${item.dv ? `-${item.dv}` : ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.softwareClassificationCode || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.commercialClassificationCode || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.email || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.phone || item.primaryNotificationPhone || ""}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;

    await html2pdf()
      .set({
        margin: 8,
        filename: `clientes-${new Date().toISOString().slice(0, 10)}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
      })
      .from(wrapper)
      .save();
  };

  const handleExport = async format => {
    try {
      setExporting(true);
      const { data } = await api.get("/clients/export", {
        params: {
          searchParam: debouncedSearchParam,
          softwareClassification,
          commercialClassification
          ,
          activeFilter,
          sortBy,
          sortDirection
        }
      });

      const rows = Array.isArray(data) ? data : [];
      if (!rows.length) {
        toast.warning("No hay clientes para exportar.");
        return;
      }

      if (format === "xlsx") {
        exportExcel(rows);
      } else {
        await exportPdf(rows);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setExporting(false);
    }
  };

  const reloadDetailRecord = async clientId => {
    const { data } = await api.get(`/clients/${clientId}`);
    setDetailRecord(data);
  };

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchParam(searchParam);
      setPageNumber(1);
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchParam]);

  useEffect(() => {
    setPageNumber(1);
  }, [softwareClassification, commercialClassification, activeFilter, sortBy, sortDirection, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [debouncedSearchParam, softwareClassification, commercialClassification, activeFilter, sortBy, sortDirection, pageNumber, pageSize]);

  const openCreateClientModal = () => {
    setClientForm(emptyClientForm);
    setClientModalOpen(true);
  };

  const openEditClientModal = async record => {
    let detailData = null;

    try {
      const { data } = await api.get(`/clients/${record.id}`);
      detailData = data;
    } catch (_error) {
      detailData = null;
    }

    setClientForm({
      id: record.id,
      name: record.name || "",
      identificationNumber: record.identificationNumber || "",
      installationCode: record.installationCode || detailData?.installationCode || "",
      dv: record.dv || "",
      email: record.email || "",
      phone: record.phone || "",
      address: record.address || "",
      stateName: record.stateName || "",
      merchantRegistration: record.merchantRegistration || "",
      softwareClassificationCode: record.softwareClassificationCode || "",
      commercialClassificationCode: record.commercialClassificationCode || "",
      catalogSelections: detailData?.editableCatalogSelections || emptyClientForm.catalogSelections,
      primaryNotificationPhone: record.primaryNotificationPhone || "",
      primaryNotificationEmail: record.primaryNotificationEmail || "",
      notes: record.notes || "",
      isActive: record.isActive !== false
    });
    setClientModalOpen(true);
  };

  const handleSaveClient = async () => {
    try {
      const payload = {
        ...clientForm,
        name: String(clientForm.name || "").trim(),
        identificationNumber: String(clientForm.identificationNumber || "").trim()
      };

      if (!payload.name || !payload.identificationNumber) {
        toast.error("Nombre y NIT o cedula son obligatorios.");
        return;
      }

      const originalRecord = records.find(record => String(record.id) === String(clientForm.id));
      const statusChanged =
        clientForm.id &&
        originalRecord &&
        (originalRecord.isActive !== false) !== (payload.isActive !== false);

      if (statusChanged) {
        setClientStatusConfirmation({
          payload,
          from: originalRecord.isActive !== false ? "Activo" : "Inactivo",
          to: payload.isActive !== false ? "Activo" : "Inactivo"
        });
        return;
      }

      setClientSaving(true);

      if (clientForm.id) {
        await api.put(`/clients/${clientForm.id}`, payload);
        toast.success("Cliente actualizado.");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente creado.");
      }

      setClientModalOpen(false);
      await loadRecords(true);
    } catch (error) {
      toastError(error);
    } finally {
      setClientSaving(false);
    }
  };

  const executeSaveClientWithApiStatus = async () => {
    if (!clientStatusConfirmation?.payload?.id) return;

    try {
      setClientSaving(true);
      const payload = clientStatusConfirmation.payload;
      const { data } = await api.put(`/clients/${payload.id}/api-operations/status`, {
        confirm: true,
        active: payload.isActive !== false
      });

      await api.put(`/clients/${payload.id}`, payload);
      toast.success(data?.message || "Estado actualizado en las APIs.");
      setClientStatusConfirmation(null);
      setClientModalOpen(false);
      await loadRecords(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible cambiar el estado en las APIs.");
    } finally {
      setClientSaving(false);
    }
  };

  const openDetailModal = async clientId => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const { data } = await api.get(`/clients/${clientId}`);
      setDetailRecord(data);
    } catch (error) {
      setDetailOpen(false);
      toastError(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const openApiTokensDialog = async record => {
    closeClientActionMenu();
    try {
      setApiTokensOpen(true);
      setApiTokensLoading(true);
      setApiTokensCustomer(record);
      setApiTokensData(null);
      const { data } = await api.get(`/clients/${record.id}/api-tokens`);
      setApiTokensData(data);
    } catch (error) {
      setApiTokensOpen(false);
      toast.error(error?.response?.data?.message || "No fue posible consultar los tokens de empresa.");
    } finally {
      setApiTokensLoading(false);
    }
  };

  const copyApiToken = async token => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(token);
      } else {
        const input = document.createElement("textarea");
        input.value = token;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      toast.success("Token copiado.");
    } catch (_error) {
      toast.error("No fue posible copiar el token. Copielo manualmente.");
    }
  };

  const loadResolutionHistory = async clientId => {
    const { data } = await api.get(`/clients/${clientId}/resolutions`);
    setResolutionHistory(Array.isArray(data) ? data : []);
  };

  const closeClientActionMenu = () => {
    setClientActionAnchor(null);
    setClientActionRecord(null);
  };

  const openResolutionDialog = async record => {
    closeClientActionMenu();
    try {
      setResolutionLoading(true);
      setResolutionCustomer(record);
      setResolutionFile(null);
      setSyncResolutionToNube(false);
      setNubeSyncConfirmation(null);
      setResolutionHistory([]);
      setResolutionDialogOpen(true);
      const [{ data: customer }, { data: history }] = await Promise.all([
        api.get(`/clients/${record.id}`),
        api.get(`/clients/${record.id}/resolutions`)
      ]);
      setResolutionCustomer(customer);
      setResolutionHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      setResolutionDialogOpen(false);
      toastError(error);
    } finally {
      setResolutionLoading(false);
    }
  };

  const handleResolutionFileChange = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await inspectResolutionPdf(file);
      setResolutionFile(file);
    } catch (error) {
      event.target.value = "";
      setResolutionFile(null);
      toast.error(error?.message || "No fue posible validar el PDF seleccionado.");
    }
  };

  const processResolution = async () => {
    if (!resolutionCustomer?.id || !resolutionFile) {
      toast.warning("Seleccione primero un PDF oficial de resolucion DIAN.");
      return;
    }

    try {
      setResolutionProcessing(true);
      const pdfBase64 = await fileToBase64(resolutionFile);
      const { data } = await api.post(`/clients/${resolutionCustomer.id}/resolutions/process`, {
        pdf_base64: pdfBase64,
        fileName: resolutionFile.name
      });
      if (data?.pending) {
        toast.warning(data?.message || "La resolucion quedo pendiente para reintento.");
      } else {
        toast.success(data?.message || "Resolucion procesada correctamente.");
      }
      if (!data?.pending && syncResolutionToNube && data?.resolution?.id) {
        try {
          const { data: preview } = await api.post(
            `/clients/${resolutionCustomer.id}/resolutions/${data.resolution.id}/nube-sync/preview`
          );
          const status = String(preview?.status || "");
          if (["ready_to_create", "ready_to_replace"].includes(status)) {
            setNubeSyncConfirmation({ resolution: data.resolution, preview });
          } else {
            toast.info(preview?.message || "La validacion con FacilWeb Nube fue registrada.");
          }
        } catch (previewError) {
          toast.warning(previewError?.response?.data?.message || "La resolucion se proceso, pero no fue posible validar FacilWeb Nube.");
        }
      }
      setResolutionFile(null);
      await loadResolutionHistory(resolutionCustomer.id);
    } catch (error) {
      const message = error?.response?.data?.message || "No fue posible procesar la resolucion.";
      toast.error(message);
      if (resolutionCustomer?.id) {
        await loadResolutionHistory(resolutionCustomer.id).catch(() => undefined);
      }
    } finally {
      setResolutionProcessing(false);
    }
  };

  const applyNubeResolutionSync = async () => {
    if (!resolutionCustomer?.id || !nubeSyncConfirmation?.resolution?.id) return;
    try {
      setNubeSyncApplying(true);
      const { data } = await api.post(
        `/clients/${resolutionCustomer.id}/resolutions/${nubeSyncConfirmation.resolution.id}/nube-sync/apply`
      );
      toast.success(data?.message || "Resolucion sincronizada con FacilWeb Nube.");
      setNubeSyncConfirmation(null);
      await loadResolutionHistory(resolutionCustomer.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible sincronizar la resolucion con FacilWeb Nube.");
      await loadResolutionHistory(resolutionCustomer.id).catch(() => undefined);
    } finally {
      setNubeSyncApplying(false);
    }
  };

  const retryResolution = async resolution => {
    if (!resolutionCustomer?.id) return;
    try {
      setResolutionProcessing(true);
      const { data } = await api.post(
        `/clients/${resolutionCustomer.id}/resolutions/${resolution.id}/retry`
      );
      if (data?.pending) {
        toast.warning(data?.message || "La resolucion sigue pendiente para reintento.");
      } else {
        toast.success(data?.message || "Reintento procesado correctamente.");
      }
      await loadResolutionHistory(resolutionCustomer.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible reintentar la resolucion.");
      await loadResolutionHistory(resolutionCustomer.id).catch(() => undefined);
    } finally {
      setResolutionProcessing(false);
    }
  };

  const openResolutionDetail = async resolution => {
    if (!resolutionCustomer?.id) return;
    try {
      setResolutionDetailOpen(true);
      setResolutionDetail(null);
      const { data } = await api.get(
        `/clients/${resolutionCustomer.id}/resolutions/${resolution.id}`
      );
      setResolutionDetail(data);
    } catch (error) {
      setResolutionDetailOpen(false);
      toastError(error);
    }
  };

  const refreshApiOperations = async customerId => {
    const { data: status } = await api.get(`/clients/${customerId}/api-operations/status`);
    const { data: customer } = await api.get(`/clients/${customerId}`);
    setApiOperationsCustomer(customer);
    setApiOperationsStatus(status);
    setApiEnvironmentForm({
      invoice_environment: catalogEnvironmentValue(customer, "environment"),
      payroll_environment: catalogEnvironmentValue(customer, "payroll_environment"),
      eqdocs_environment: ""
    });

    try {
      const { data: plans } = await api.get(`/clients/${customerId}/api-operations/plans`);
      setApiOperationsPlans(plans);
    } catch (error) {
      setApiOperationsPlans({
        error: error?.response?.data?.message || "No fue posible consultar planes para este cliente."
      });
    }
  };

  const openApiOperationsDialog = async record => {
    closeClientActionMenu();
    try {
      setApiOperationsOpen(true);
      setApiOperationsLoading(true);
      setApiOperationsCustomer(record);
      setApiOperationsStatus(null);
      setApiOperationsPlans(null);
      setApiOperationsTab(0);
      setApiPlanForm(emptyApiPlanForm);
      setApiEnvironmentConfirmation(null);
      await refreshApiOperations(record.id);
    } catch (error) {
      setApiOperationsOpen(false);
      toast.error(error?.response?.data?.message || "No fue posible consultar ambientes y planes del cliente.");
    } finally {
      setApiOperationsLoading(false);
    }
  };

  const refreshApiPrefixes = async (customerId, sourceId = "") => {
    setApiPrefixesData(null);
    setResolutionAssignments({ links: [], establishments: [] });
    const { data: status } = await api.get(`/clients/${customerId}/api-operations/status`);
    const selectedSourceId = sourceId || status?.selectedSourceId || "";
    setApiPrefixesStatus(status);
    setApiPrefixesSourceId(selectedSourceId ? String(selectedSourceId) : "");

    const params = selectedSourceId ? { sourceId: selectedSourceId } : {};
    const { data: prefixes } = await api.get(`/clients/${customerId}/api-operations/prefixes`, { params });
    const { data: assignments } = await api.get(`/clients/${customerId}/resolution-establishments`, { params });
    setResolutionAssignments(assignments);
    setApiPrefixesData(prefixes);
  };

  const saveResolutionEstablishment = async (row, establishmentId) => {
    setResolutionAssignmentSaving(true);
    try {
      const { data: link } = await api.put(`/clients/${apiPrefixesCustomer.id}/resolution-establishments/${row.id}`, {
        sourceId: apiPrefixesSourceId,
        customerEstablishmentId: establishmentId === "" ? null : Number(establishmentId)
      });
      setResolutionAssignments(current => ({
        ...current,
        links: [...current.links.filter(item => String(item.externalResolutionId) !== String(row.id)), link]
      }));
      toast.success(establishmentId === "" ? "Asignacion retirada." : "Establecimiento asignado.");
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible guardar la asignacion.");
      return false;
    } finally {
      setResolutionAssignmentSaving(false);
    }
  };

  const resolutionFingerprint = row => JSON.stringify([
    String(row?.type_document_id ?? ""), String(row?.prefix || "").trim().toUpperCase(),
    String(row?.resolution || "").trim(), String(row?.from ?? ""), String(row?.to ?? ""),
    String(row?.date_from || "").slice(0, 10), String(row?.date_to || "").slice(0, 10)
  ]);

  const previewResolutionCopy = async row => {
    const branchId = resolutionAssignments.links.find(link => String(link.externalResolutionId) === String(row.id))?.customerEstablishmentId;
    if (!branchId) return;
    const branch = resolutionAssignments.establishments.find(item => item.id === branchId);
    if (!branch?.isActive) { toast.warning("Seleccione un establecimiento activo."); return; }
    setResolutionAssignmentSaving(true);
    setResolutionCopy({ busy: true, rows: [], branchId, branchName: branch.name });
    const targets = [];
    for (const source of (apiPrefixesStatus?.matches || []).filter(item => String(item.sourceId) !== String(apiPrefixesSourceId))) {
      try {
        const params = { sourceId: source.sourceId };
        const { data: prefixes } = await api.get(`/clients/${apiPrefixesCustomer.id}/api-operations/prefixes`, { params });
        const { data: assignments } = await api.get(`/clients/${apiPrefixesCustomer.id}/resolution-establishments`, { params });
        const matches = (prefixes?.result?.prefixes || []).filter(item => resolutionFingerprint(item) === resolutionFingerprint(row));
        const target = matches.length === 1 ? matches[0] : null;
        const existing = target && assignments.links.find(link => String(link.externalResolutionId) === String(target.id))?.customerEstablishmentId;
        const ready = Boolean(target && !existing);
        targets.push({ sourceId: source.sourceId, name: source.sourceName, target, ready,
          message: !target ? (matches.length > 1 ? "Coincidencia ambigua: no se aplicara" : "Sin resolucion equivalente") : existing === branchId ? "Ya tiene esta sucursal" : existing ? "Tiene otra sucursal: no se sobrescribira" : "Lista para aplicar" });
      } catch (error) {
        targets.push({ sourceId: source.sourceId, name: source.sourceName, ready: false, message: "No se pudo consultar. Reintente mas tarde." });
      }
    }
    setResolutionCopy({ busy: false, rows: targets, branchId, branchName: branch.name, customerId: apiPrefixesCustomer.id });
    setResolutionAssignmentSaving(false);
  };

  const applyResolutionCopy = async () => {
    if (!resolutionCopy || resolutionCopy.busy) return;
    setResolutionAssignmentSaving(true);
    setResolutionCopy(current => ({ ...current, busy: true }));
    const results = [];
    for (const item of resolutionCopy.rows) {
      if (!item.ready) { results.push(item); continue; }
      try {
        await api.put(`/clients/${resolutionCopy.customerId}/resolution-establishments/${item.target.id}`, {
          sourceId: item.sourceId, customerEstablishmentId: resolutionCopy.branchId,
          expectedPreviousEstablishmentId: null, expectedResolution: item.target
        });
        results.push({ ...item, ready: false, message: "Sucursal aplicada correctamente" });
      } catch (error) {
        results.push({ ...item, ready: false, message: error?.response?.data?.message || "No fue posible aplicar. Consulte de nuevo." });
      }
    }
    setResolutionCopy(current => ({ ...current, busy: false, done: true, rows: results }));
    setResolutionAssignmentSaving(false);
  };

  const openApiPrefixesDialog = async record => {
    closeClientActionMenu();
    try {
      setApiPrefixesOpen(true);
      setApiPrefixesLoading(true);
      setApiPrefixesCustomer(record);
      setApiPrefixesStatus(null);
      setApiPrefixesData(null);
      setApiPrefixesSourceId("");
      await refreshApiPrefixes(record.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible consultar prefijos.");
    } finally {
      setApiPrefixesLoading(false);
    }
  };

  const handleApiPrefixesSourceChange = async event => {
    const nextSourceId = event.target.value;
    if (!apiPrefixesCustomer?.id) return;
    try {
      setApiPrefixesLoading(true);
      await refreshApiPrefixes(apiPrefixesCustomer.id, nextSourceId);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible consultar prefijos de esa API.");
    } finally {
      setApiPrefixesLoading(false);
    }
  };

  const openApiPrefixEditor = (row = null) => {
    if (row?.is_dian_resolution || row?.editable_from_soluwats === false) {
      toast.warning("Las resoluciones DIAN son solo de consulta. Solo se editan prefijos manuales.");
      return;
    }

    setApiPrefixForm(row ? {
      id: row.id || null,
      resolution: row.resolution == null ? "" : String(row.resolution),
      type_document_id: row.type_document_id ? String(row.type_document_id) : "",
      prefix: row.prefix || "",
      from: row.from ?? "",
      to: row.to ?? "",
      date_from: row.date_from ? String(row.date_from).slice(0, 10) : "",
      date_to: row.date_to ? String(row.date_to).slice(0, 10) : ""
    } : {
      ...emptyApiPrefixForm,
      date_from: new Date().toISOString().slice(0, 10)
    });
    setApiPrefixEditorOpen(true);
  };

  const handleSaveApiPrefix = () => {
    if (process.env.REACT_APP_CUSTOMER_SOFTWARE_DEV === 'true' && [4, 5].includes(Number(apiPrefixForm.type_document_id)) &&
        apiPrefixForm.resolution && !/^\d{1,20}$/.test(apiPrefixForm.resolution)) {
      toast.warning("La resolucion interna debe contener de 1 a 20 digitos.");
      return;
    }
    if (!apiPrefixesCustomer?.id) return;
    if (!apiPrefixesSourceId) {
      toast.warning("Seleccione la API donde se guardara el prefijo.");
      return;
    }
    if (!apiPrefixForm.type_document_id) {
      toast.warning("Seleccione el tipo de documento.");
      return;
    }
    if (!String(apiPrefixForm.prefix || "").trim()) {
      toast.warning("Indique el prefijo.");
      return;
    }

    setApiPrefixConfirmation({
      action: apiPrefixForm.id ? "update" : "create",
      detail: apiPrefixForm.id
        ? "Se actualizara este prefijo manual en la API seleccionada."
        : "Se creara un nuevo prefijo manual en la API seleccionada."
    });
  };

  const executeSaveApiPrefix = async () => {
    if (!apiPrefixesCustomer?.id || !apiPrefixConfirmation) return;
    try {
      setApiPrefixSaving(true);
      const { data } = await api.post(`/clients/${apiPrefixesCustomer.id}/api-operations/prefixes`, {
        confirm: true,
        sourceId: apiPrefixesSourceId,
        id: apiPrefixForm.id || undefined,
        type_document_id: Number(apiPrefixForm.type_document_id),
        prefix: String(apiPrefixForm.prefix || "").toUpperCase().trim(),
        ...(process.env.REACT_APP_CUSTOMER_SOFTWARE_DEV === 'true' && [4, 5].includes(Number(apiPrefixForm.type_document_id))
          ? { resolution: apiPrefixForm.resolution || null } : {}),
        from: apiPrefixForm.from !== "" ? Number(apiPrefixForm.from) : 0,
        to: apiPrefixForm.to !== "" ? Number(apiPrefixForm.to) : 0,
        date_from: apiPrefixForm.date_from || null,
        date_to: apiPrefixForm.date_to || null
      });
      toast.success(data?.message || "Prefijo guardado.");
      setApiPrefixConfirmation(null);
      setApiPrefixEditorOpen(false);
      setApiPrefixForm(emptyApiPrefixForm);
      if (data?.refreshed) {
        setApiPrefixesData(prev => ({ ...(prev || {}), result: data.refreshed }));
      } else {
        await refreshApiPrefixes(apiPrefixesCustomer.id, apiPrefixesSourceId);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible guardar el prefijo.");
    } finally {
      setApiPrefixSaving(false);
    }
  };

  const buildApiEnvironmentPreview = () => {
    const invoiceCurrent = catalogEnvironmentValue(apiOperationsCustomer, "environment");
    const payrollCurrent = catalogEnvironmentValue(apiOperationsCustomer, "payroll_environment");
    const invoiceTarget = apiEnvironmentForm.invoice_environment || invoiceCurrent;
    const payrollTarget = apiEnvironmentForm.payroll_environment || payrollCurrent;
    const changes = [
      apiEnvironmentForm.invoice_environment
        ? {
            service: "Factura electronica",
            from: environmentLabel(invoiceCurrent),
            to: environmentLabel(apiEnvironmentForm.invoice_environment)
          }
        : null,
      apiEnvironmentForm.payroll_environment
        ? {
            service: "Nomina",
            from: environmentLabel(payrollCurrent),
            to: environmentLabel(apiEnvironmentForm.payroll_environment)
          }
        : null
    ].filter(Boolean);

    return {
      changes,
      detail:
        `Factura electronica quedara en ${environmentLabel(invoiceTarget)}. ` +
        `Documento soporte se sincroniza con factura electronica. ` +
        `Nomina quedara en ${environmentLabel(payrollTarget)}. ` +
        `El cambio se enviara a todas las APIs donde este cliente aparezca.`
    };
  };

  const executeSaveApiEnvironments = async () => {
    if (!apiOperationsCustomer?.id || !apiEnvironmentConfirmation) return;
    try {
      setApiOperationsSaving(true);
      const { data } = await api.put(`/clients/${apiOperationsCustomer.id}/api-operations/environments`, {
        confirm: true,
        ...apiEnvironmentForm
      });
      toast.success(data?.message || "Ambientes actualizados.");
      setApiEnvironmentConfirmation(null);
      await refreshApiOperations(apiOperationsCustomer.id);
      await loadRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible actualizar ambientes.");
    } finally {
      setApiOperationsSaving(false);
    }
  };

  const handleSaveApiEnvironments = async () => {
    if (!apiOperationsCustomer?.id) return;
    const hasChange = Object.values(apiEnvironmentForm).some(Boolean);
    if (!hasChange) {
      toast.warning("Seleccione al menos un ambiente para enviar a la API.");
      return;
    }

    setApiEnvironmentConfirmation(buildApiEnvironmentPreview());
  };

  const buildApiPlanAssignmentPreview = () => {
    const serviceLabel = PLAN_SERVICE_LABELS[apiPlanForm.service] || apiPlanForm.service;
    if (isAbsolutePlanService) {
      return {
        serviceLabel,
        targetLabel: `${formatPlanNumber(apiPlanForm.absolut_plan_documents)} folios`,
        detail: "Se asignara un paquete libre de folios a la API del cliente."
      };
    }

    return {
      serviceLabel,
      targetLabel: apiSelectedPlanOption?.label || `Plan ${apiPlanForm.plan_id}`,
      detail: "Se asignara o renovara el plan seleccionado en la API del cliente."
    };
  };

  const handleAssignApiPlan = () => {
    if (!apiOperationsCustomer?.id) return;
    if (!apiPlanForm.service) {
      toast.warning("Seleccione el servicio del plan.");
      return;
    }
    if (isAbsolutePlanService) {
      if (!apiPlanForm.absolut_plan_documents) {
        toast.warning("Indique la cantidad del paquete de folios.");
        return;
      }
    } else if (!apiPlanForm.plan_id) {
      toast.warning("Seleccione un plan predefinido.");
      return;
    }

    setApiPlanConfirmation(buildApiPlanAssignmentPreview());
  };

  const executeAssignApiPlan = async () => {
    if (!apiOperationsCustomer?.id || !apiPlanConfirmation) return;
    try {
      setApiOperationsSaving(true);
      const { data } = await api.post(`/clients/${apiOperationsCustomer.id}/api-operations/plans/assign`, {
        confirm: true,
        service: apiPlanForm.service,
        plan_id: !isAbsolutePlanService && apiPlanForm.plan_id ? Number(apiPlanForm.plan_id) : undefined,
        absolut_plan_documents: isAbsolutePlanService && apiPlanForm.absolut_plan_documents
          ? Number(apiPlanForm.absolut_plan_documents)
          : undefined,
        renew: apiPlanForm.renew,
        notes: apiPlanForm.notes
      });
      toast.success(data?.message || "Plan o folios asignados.");
      setApiPlanConfirmation(null);
      setApiPlanForm(emptyApiPlanForm);
      await refreshApiOperations(apiOperationsCustomer.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible asignar plan o folios.");
    } finally {
      setApiOperationsSaving(false);
    }
  };

  const handleSyncAllApiOperations = async () => {
    try {
      setSyncAllApiLoading(true);
      const { data: preview } = await api.post("/clients/api-operations/sync-all", { dryRun: true });
      const confirmed = window.confirm(
        `Revision terminada: ${preview.matchedCustomers || 0} de ${preview.totalCustomers || 0} clientes aparecen en APIs activas. ` +
        "Si confirma, SoluWats copiara desde la API el ambiente actual de FE, nomina y documento soporte, usando el registro mas reciente si el cliente aparece en varias APIs. No cambia nada en las APIs. Desea aplicar?"
      );
      if (!confirmed) {
        toast.info("Revision realizada sin guardar cambios.");
        return;
      }

      const { data } = await api.post("/clients/api-operations/sync-all", { dryRun: false });
      toast.success(data?.message || "Sincronizacion aplicada.");
      await loadRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible sincronizar estados desde las APIs.");
    } finally {
      setSyncAllApiLoading(false);
    }
  };

  const openCreateEstablishmentModal = () => {
    setEstablishmentForm(emptyEstablishmentForm);
    setEstablishmentModalOpen(true);
  };

  const openEditEstablishmentModal = establishment => {
    setEstablishmentForm({
      id: establishment.id,
      installationCode: establishment.installationCode || "",
      name: establishment.name || "",
      address: establishment.address || "",
      departmentCode: establishment.departmentCode || "",
      departmentName: establishment.departmentName || "",
      municipalityCode: establishment.municipalityCode || "",
      municipalityName: establishment.municipalityName || "",
      primaryPhone: establishment.primaryPhone || "",
      primaryEmail: establishment.primaryEmail || "",
      notes: establishment.notes || "",
      isActive: establishment.isActive !== false
    });
    setEstablishmentModalOpen(true);
  };

  const handleSaveEstablishment = async () => {
    try {
      if (!detailRecord?.id) {
        return;
      }

      const payload = {
        ...establishmentForm,
        name: String(establishmentForm.name || "").trim()
      };

      if (!payload.name) {
        toast.error("El nombre del establecimiento es obligatorio.");
        return;
      }

      setEstablishmentSaving(true);

      if (establishmentForm.id) {
        await api.put(`/clients/${detailRecord.id}/establishments/${establishmentForm.id}`, payload);
        toast.success("Establecimiento actualizado.");
      } else {
        await api.post(`/clients/${detailRecord.id}/establishments`, payload);
        toast.success("Establecimiento creado.");
      }

      setEstablishmentModalOpen(false);
      await reloadDetailRecord(detailRecord.id);
      await loadRecords(true);
    } catch (error) {
      toastError(error);
    } finally {
      setEstablishmentSaving(false);
    }
  };

  const departmentOptions = useMemo(
    () =>
      [...(geoCatalogs.departments || [])].sort((left, right) =>
        String(left.name || "").localeCompare(String(right.name || ""), "es", {
          sensitivity: "base",
          numeric: true
        })
      ),
    [geoCatalogs.departments]
  );

  const municipalityOptions = useMemo(() => {
    const selectedDepartmentCode = String(establishmentForm.departmentCode || "").trim();
    const rows = [...(geoCatalogs.municipalities || [])];

    const filtered = selectedDepartmentCode
      ? rows.filter(item => String(item.departmentCode || "") === selectedDepartmentCode)
      : rows;

    return filtered.sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || ""), "es", {
        sensitivity: "base",
        numeric: true
      })
    );
  }, [establishmentForm.departmentCode, geoCatalogs.municipalities]);

  const selectedDepartment = useMemo(
    () => departmentOptions.find(item => String(item.code) === String(establishmentForm.departmentCode || "")) || null,
    [departmentOptions, establishmentForm.departmentCode]
  );

  const selectedMunicipality = useMemo(
    () => municipalityOptions.find(item => String(item.code) === String(establishmentForm.municipalityCode || "")) || null,
    [municipalityOptions, establishmentForm.municipalityCode]
  );

  const getAttributeOptions = catalogType =>
    Array.isArray(catalogs.attributes?.[catalogType]) ? catalogs.attributes[catalogType] : [];

  const handleEditableCatalogChange = (catalogType, selectedCode) => {
    const options = getAttributeOptions(catalogType);
    const selected = options.find(
      option => String(option.code || option.name || "") === String(selectedCode || "")
    );

    setClientForm(prev => ({
      ...prev,
      catalogSelections: {
        ...(prev.catalogSelections || {}),
        [catalogType]: selected
          ? { code: selected.code || "", name: selected.name || selected.code || "" }
          : null
      }
    }));
  };

  const handleLinkPossibleContacts = async () => {
    try {
      if (!detailRecord?.id) {
        return;
      }

      const { data } = await api.post(`/clients/${detailRecord.id}/link-contacts`);
      toast.success(
        `Contactos vinculados: ${Number(data?.linkedContacts || 0)}. Tickets: ${Number(data?.linkedTickets || 0)}. Tareas: ${Number(data?.linkedTasks || 0)}.`
      );
      await reloadDetailRecord(detailRecord.id);
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const handleManualAssignContacts = async () => {
    try {
      if (!detailRecord?.id || !contactLinkSelection.length) {
        toast.warning("Seleccione al menos un contacto.");
        return;
      }

      const { data } = await api.post(`/clients/${detailRecord.id}/assign-contacts`, {
        contactIds: contactLinkSelection.map(item => item.id)
      });

      toast.success(
        `Contactos relacionados: ${Number(data?.linkedContacts || 0)}. Tickets: ${Number(data?.linkedTickets || 0)}. Tareas: ${Number(data?.linkedTasks || 0)}.`
      );
      setContactLinkDialog(false);
      setContactLinkSelection([]);
      await reloadDetailRecord(detailRecord.id);
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteEstablishment = async establishment => {
    try {
      if (!detailRecord?.id || !establishment?.id) {
        return;
      }

      const confirmed = window.confirm(
        `Va a eliminar el establecimiento "${establishment.name || "sin nombre"}". Esta accion no se puede deshacer.`
      );

      if (!confirmed) {
        return;
      }

      await api.delete(`/clients/${detailRecord.id}/establishments/${establishment.id}`);
      toast.success("Establecimiento eliminado.");
      await reloadDetailRecord(detailRecord.id);
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const openContactLinkDialog = async () => {
    try {
      if (!detailRecord?.id) {
        return;
      }

      setContactLinkLoading(true);
      setContactLinkDialog(true);

      const { data } = await api.get("/contacts/list");
      const relatedIds = new Set((detailRecord.contacts || []).map(item => Number(item.id)));

      const options = (Array.isArray(data) ? data : [])
        .filter(item => !item?.isGroup)
        .filter(item => Number(item?.businessCustomerId || 0) !== Number(detailRecord.id))
        .filter(item => {
          const number = String(item?.number || "").trim();
          const name = String(item?.name || "").trim();

          if (hasUsableClientContactName(item)) return true;
          if (!number) return false;
          if (!/^\d+$/.test(number)) return true;

          return number.length <= 15 && name !== number;
        })
        .filter(item => !relatedIds.has(Number(item.id)))
        .sort((left, right) =>
          buildClientContactLabel(left).localeCompare(buildClientContactLabel(right), "es", {
            sensitivity: "base",
            numeric: true
          })
        );

      setContactLinkOptions(options);
    } catch (error) {
      setContactLinkDialog(false);
      toastError(error);
    } finally {
      setContactLinkLoading(false);
    }
  };

  const openRutOnboarding = async () => {
    try {
      setRutOnboardingOpen(true);
      const savedDraft = readRutOnboardingDraft();
      setRutOnboardingForm(savedDraft ? { ...emptyRutOnboardingForm, ...savedDraft } : emptyRutOnboardingForm);
      setRutOnboardingDraftRestored(Boolean(savedDraft));
      setRutOnboardingResult(null);
      const { data } = await api.get("/clients/sources/alta-rut");
      const availableSources = Array.isArray(data) ? data : [];
      setRutSources(availableSources);
      const firstEnabled = availableSources.find(source => source.enabledForRutOnboarding);
      if (firstEnabled) {
        setRutOnboardingForm(prev => ({ ...prev, sourceId: String(firstEnabled.id) }));
      }
    } catch (error) {
      setRutOnboardingOpen(false);
      toastError(error);
    }
  };

  const updateRutFile = async (field, file) => {
    if (!file) return;
    if (field === "resolutionFiles") {
      try {
        const inspectedFile = await inspectResolutionPdf(file);
        setRutOnboardingForm(prev => {
          const exists = prev.resolutionFiles.some(item => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified);
          if (exists) return prev;
          return { ...prev, resolutionFiles: [...prev.resolutionFiles, inspectedFile] };
        });
        toast.success(`Resolucion validada: ${file.name}`);
      } catch (error) {
        toast.error(error.message || "No fue posible validar la resolucion.");
      }
      return;
    }
    if (field === "rutFile" && (!/\.pdf$/i.test(file.name) || file.size > 8 * 1024 * 1024)) {
      toast.warning("El RUT debe ser un PDF de maximo 8 MB.");
      return;
    }
    if (field === "logoFile" && (!/^image\/(png|jpeg)$/.test(file.type) || file.size > 2 * 1024 * 1024)) {
      toast.warning("El logo debe ser JPG o PNG de maximo 2 MB.");
      return;
    }
    setRutOnboardingForm(prev => ({ ...prev, [field]: file }));
  };

  const removeResolutionFile = index => {
    setRutOnboardingForm(prev => ({
      ...prev,
      resolutionFiles: prev.resolutionFiles.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const clearRutOnboarding = () => {
    clearRutOnboardingDraft();
    setRutOnboardingForm(emptyRutOnboardingForm);
    setRutOnboardingResult(null);
    setRutOnboardingDraftRestored(false);
    toast.info("Formulario limpiado.");
  };

  const parseOptionalJson = (value, label) => {
    if (!String(value || "").trim()) return undefined;
    try {
      return JSON.parse(value);
    } catch (_error) {
      throw new Error(`${label} debe ser JSON valido.`);
    }
  };

  const submitRutOnboarding = async createMode => {
    try {
      const selectedSource = rutSources.find(source => String(source.id) === String(rutOnboardingForm.sourceId));
      if (!selectedSource?.enabledForRutOnboarding) {
        toast.warning("Seleccione una API habilitada para altas desde RUT.");
        return;
      }
      if (!rutOnboardingForm.rutFile) {
        toast.warning("Adjunte primero el PDF del RUT.");
        return;
      }
      if (createMode && !rutOnboardingForm.confirmCreate) {
        toast.warning("Confirme que desea crear o completar la empresa en la API seleccionada.");
        return;
      }
      if (createMode && !String(rutOnboardingForm.softwareId || "").trim()) {
        toast.warning("Pegue el Software ID de DIAN antes de crear la empresa.");
        return;
      }
      if (createMode && !String(rutOnboardingForm.softwareClassificationCode || "").trim()) {
        toast.warning("Seleccione la clasificación de software antes de crear.");
        return;
      }
      if (createMode && !String(rutOnboardingForm.commercialClassificationCode || "").trim()) {
        toast.warning("Seleccione la clasificación comercial antes de crear.");
        return;
      }
      if (rutOnboardingForm.runInvoiceTests && !String(rutOnboardingForm.invoiceTestSetId || "").trim()) {
        toast.warning("Para ejecutar pruebas de factura indique el Set de pruebas DIAN.");
        return;
      }

      setRutOnboardingSaving(true);
      const [pdfBase64, certificateBase64, logoBase64] = await Promise.all([
        fileToBase64(rutOnboardingForm.rutFile),
        rutOnboardingForm.certificateFile ? fileToBase64(rutOnboardingForm.certificateFile) : Promise.resolve(undefined),
        rutOnboardingForm.logoFile ? fileToBase64(rutOnboardingForm.logoFile) : Promise.resolve(undefined)
      ]);

      const payload = {
        pdf_base64: pdfBase64,
        dry_run: !createMode,
        create: createMode,
        update_existing: createMode && rutOnboardingForm.updateExisting,
        complete_existing_if_not_production: createMode && rutOnboardingForm.completeExisting,
        services: { invoice: rutOnboardingForm.invoice, payroll: rutOnboardingForm.payroll, eqdocs: rutOnboardingForm.eqdocs },
        local_software_classification: rutOnboardingForm.softwareClassificationCode,
        local_commercial_classification: rutOnboardingForm.commercialClassificationCode,
        software_name: rutOnboardingForm.softwareName || undefined,
        software_id: rutOnboardingForm.softwareId.trim() || undefined,
        software_pin: rutOnboardingForm.softwarePin.trim() || "12345",
        certificate_base64: certificateBase64,
        certificate_password: rutOnboardingForm.certificatePassword || undefined,
        logo_base64: logoBase64,
        setup_environment: rutOnboardingForm.setupEnvironment,
        type_environment_id: 2,
        payroll_type_environment_id: 2,
        eqdocs_type_environment_id: 2,
        setup_test_resolutions: rutOnboardingForm.setupTestResolutions,
        run_invoice_tests: createMode && rutOnboardingForm.runInvoiceTests,
        run_payroll_tests: createMode && rutOnboardingForm.runPayrollTests,
        invoice_test_set_id: rutOnboardingForm.invoiceTestSetId || undefined,
        payroll_test_set_id: rutOnboardingForm.payrollTestSetId || undefined,
        invoice_test_start_number: rutOnboardingForm.invoiceTestStartNumber ? Number(rutOnboardingForm.invoiceTestStartNumber) : undefined,
        credit_note_test_number: rutOnboardingForm.creditNoteTestNumber ? Number(rutOnboardingForm.creditNoteTestNumber) : undefined,
        debit_note_test_number: rutOnboardingForm.debitNoteTestNumber ? Number(rutOnboardingForm.debitNoteTestNumber) : undefined,
        overrides: parseOptionalJson(rutOnboardingForm.overridesJson, "Correcciones manuales"),
        resolutions: parseOptionalJson(rutOnboardingForm.resolutionsJson, "Resoluciones personalizadas")
      };

      const { data } = await api.post(`/clients/sources/${selectedSource.id}/alta-rut`, payload);
      const result = data?.result || data;
      setRutOnboardingResult(result);
      const identificationNumber = String(
        result?.company_payload?.identification_number
        || result?.company?.identification_number
        || result?.identification_number
        || ""
      ).replace(/\D/g, "");
      if (identificationNumber) {
        setRutOnboardingForm(prev => ({
          ...prev,
          softwareName: `facilweb_${identificationNumber}`
        }));
      }
      if (createMode) {
        clearRutOnboardingDraft();
        setRutOnboardingDraftRestored(false);
      }
      toast.success(createMode ? "La API proceso el alta de la empresa." : "RUT validado. Revise el resultado antes de crear.");
      if (createMode) await loadRecords();
    } catch (error) {
      persistRutOnboardingDraft(rutOnboardingForm);
      setRutOnboardingDraftRestored(true);
      const rutErrorResult = error?.response?.data?.result || error?.response?.data || null;
      setRutOnboardingResult(rutErrorResult);
      const missingFields = formatRutMissingFields(rutErrorResult);
      if (missingFields) {
        toast.error(`El RUT fue leido, pero faltan datos para crear: ${missingFields}. Use correcciones manuales JSON o revise el PDF.`);
        return;
      }
      if (error?.message && !error?.response) toast.error(error.message);
      else toastError(error);
    } finally {
      setRutOnboardingSaving(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Clientes</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={() => loadRecords()}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleExport("xlsx")}
            disabled={exporting}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleExport("pdf")}
            disabled={exporting}
          >
            Exportar PDF
          </Button>
          {isAdmin ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={handleSyncAllApiOperations}
              disabled={syncAllApiLoading}
            >
              {syncAllApiLoading ? "Sincronizando..." : "Traer ambientes APIs"}
            </Button>
          ) : null}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreateClientModal}
          >
            Nuevo cliente
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={openRutOnboarding}
          >
            Dar de alta por RUT
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.hero}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="overline" style={{ fontWeight: 700, letterSpacing: "0.14em" }}>
              Panel comercial operativo
            </Typography>
            <Typography variant="h3" style={{ fontWeight: 800, marginTop: 8 }}>
              Clientes
            </Typography>
            <Typography variant="body1" style={{ maxWidth: 700, marginTop: 12, lineHeight: 1.7 }}>
              Este modulo centraliza clientes, relaciones operativas y futuras integraciones con
              contactos, tickets, tareas, resoluciones y automatizaciones.
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="overline">Registros visibles</Typography>
              <Typography variant="h3" style={{ fontWeight: 800, marginTop: 8 }}>
                {count}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Resultado filtrado actual.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="overline">Activos / Manuales</Typography>
              <Typography variant="h3" style={{ fontWeight: 800, marginTop: 8 }}>
                {activeCount}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {manualCount} creados manualmente.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.filterCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              label="Buscar cliente"
              value={searchParam}
              onChange={e => setSearchParam(e.target.value)}
              placeholder="Nombre, NIT, correo, telefono, direccion..."
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Software</InputLabel>
              <Select
                label="Software"
                value={softwareClassification}
                onChange={e => setSoftwareClassification(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="__none__">Sin software</MenuItem>
                {catalogs.software.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Clasificacion comercial</InputLabel>
              <Select
                label="Clasificacion comercial"
                value={commercialClassification}
                onChange={e => setCommercialClassification(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="__none__">Sin clasificación comercial</MenuItem>
                {catalogs.commercial.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={activeFilter}
                onChange={e => setActiveFilter(e.target.value)}
              >
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                label="Ordenar por"
                value={`${sortBy}:${sortDirection}`}
                onChange={e => {
                  const [nextSortBy, nextDirection] = String(e.target.value).split(":");
                  setSortBy(nextSortBy);
                  setSortDirection(nextDirection);
                }}
              >
                <MenuItem value="createdAt:DESC">Más recientes primero</MenuItem>
                <MenuItem value="createdAt:ASC">Más antiguos primero</MenuItem>
                <MenuItem value="name:ASC">Nombre A-Z</MenuItem>
                <MenuItem value="name:DESC">Nombre Z-A</MenuItem>
                <MenuItem value="software:ASC">Software A-Z</MenuItem>
                <MenuItem value="commercial:ASC">Clasificación comercial A-Z</MenuItem>
                <MenuItem value="status:DESC">Estado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchParam("");
                setSoftwareClassification("");
                setCommercialClassification("");
                setActiveFilter("active");
                setSortBy("createdAt");
                setSortDirection("DESC");
                setPageNumber(1);
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.th}>ID</TableCell>
                <TableCell className={classes.th}>Cliente</TableCell>
                <TableCell className={classes.th}>Clasificacion</TableCell>
                <TableCell className={classes.th}>Contacto</TableCell>
                <TableCell className={classes.th}>Estado</TableCell>
                <TableCell className={classes.th}>Origen</TableCell>
                <TableCell className={classes.th}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.id} className={classes.row}>
                  <TableCell className={classes.idCell}>#{record.id}</TableCell>
                  <TableCell>
                    <div className={classes.nameBlock}>
                      <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                        {record.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {record.identificationNumber}{record.dv ? `-${record.dv}` : ""}
                      </Typography>
                      {record.integrationSource?.name && (
                        <Typography variant="caption" color="textSecondary">
                          Fuente: {record.integrationSource.name}
                        </Typography>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gridGap={6}>
                      {record.softwareClassificationCode ? (
                        <Chip size="small" label={record.softwareClassificationCode} />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Sin software
                        </Typography>
                      )}
                      {record.commercialClassificationCode ? (
                        <Chip size="small" label={record.commercialClassificationCode} color="primary" />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Sin clasificacion comercial
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gridGap={4}>
                      <Typography variant="body2">{record.email || "Sin correo"}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {record.phone || record.primaryNotificationPhone || "Sin telefono"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={record.isActive ? "Activo" : "Inactivo"}
                      style={metricStyle(record.isActive)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={record.originCode || "manual"} />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="Mas acciones del cliente"
                      onClick={event => {
                        setClientActionAnchor(event.currentTarget);
                        setClientActionRecord(record);
                      }}
                      style={{ marginLeft: 4 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          component="div"
          count={count}
          page={Math.max(pageNumber - 1, 0)}
          onPageChange={(_event, nextPage) => setPageNumber(nextPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={event => {
            const nextValue = Number(event.target.value);
            setPageSize(nextValue);
            setPageNumber(1);
          }}
          rowsPerPageOptions={[
            20,
            30,
            50,
            100,
            200,
            300,
            500,
            1000,
            2000,
            { label: "Todos", value: -1 }
          ]}
          labelRowsPerPage="Mostrar"
          labelDisplayedRows={({ from, to, count: total }) =>
            `${from}-${to} de ${total === -1 ? to : total}`
          }
        />
      </Paper>

      <Menu
        anchorEl={clientActionAnchor}
        open={Boolean(clientActionAnchor)}
        onClose={closeClientActionMenu}
        keepMounted
      >
        <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) openDetailModal(record.id);
        }}>
          <VisibilityIcon fontSize="small" style={{ marginRight: 10 }} />
          Ver
        </MenuItem>
        <MenuItem onClick={() => clientActionRecord && openApiTokensDialog(clientActionRecord)}>
          <VpnKeyIcon fontSize="small" style={{ marginRight: 10 }} />
          Tokens de la empresa
        </MenuItem>
        {process.env.REACT_APP_CUSTOMER_SOFTWARE_DEV === 'true' && <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) setSoftwareCustomer(record);
        }}>Software y preparacion de nomina</MenuItem>}
        <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) setLogoCustomer(record);
        }}>Actualizar logo de empresa</MenuItem>
        <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) setExistingRegistrationCustomer(record);
        }}>Registrar en otra API / Retomar servicio</MenuItem>
        <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) openEditClientModal(record);
        }}>
          <EditIcon fontSize="small" style={{ marginRight: 10 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => clientActionRecord && openResolutionDialog(clientActionRecord)}>
          <DescriptionIcon fontSize="small" style={{ marginRight: 10 }} />
          Subir resolucion DIAN
        </MenuItem>
        <MenuItem onClick={() => clientActionRecord && openApiOperationsDialog(clientActionRecord)}>
          <ReplayIcon fontSize="small" style={{ marginRight: 10 }} />
          Ambiente real y planes
        </MenuItem>
        <MenuItem onClick={() => clientActionRecord && openApiPrefixesDialog(clientActionRecord)}>
          <DescriptionIcon fontSize="small" style={{ marginRight: 10 }} />
          Prefijos
        </MenuItem>
      </Menu>

      {existingRegistrationCustomer && <ExistingApiRegistrationDialog customer={existingRegistrationCustomer} onClose={() => setExistingRegistrationCustomer(null)} />}
      {softwareCustomer && <CustomerSoftwareDialog customer={softwareCustomer} onClose={() => setSoftwareCustomer(null)} />}
      {logoCustomer && <CompanyLogoDialog customer={logoCustomer} onClose={() => setLogoCustomer(null)} />}
      <Dialog
        open={rutOnboardingOpen}
        onClose={() => !rutOnboardingSaving && setRutOnboardingOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Dar de alta empresa desde RUT</DialogTitle>
        <DialogContent dividers>
          <Box mb={2} p={1.5} borderRadius={12} style={{ background: "rgba(28,78,125,0.08)" }}>
            <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Flujo seguro de alta</Typography>
            <Typography variant="body2" color="textSecondary">
              Primero valide el PDF de RUT. La creacion solo se habilita con confirmacion explicita y la API destino seleccionada.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>API destino</InputLabel>
                <Select
                  label="API destino"
                  value={rutOnboardingForm.sourceId}
                  onChange={e => setRutOnboardingForm(prev => ({ ...prev, sourceId: e.target.value }))}
                >
                  {rutSources.map(source => (
                    <MenuItem key={source.id} value={String(source.id)} disabled={!source.enabledForRutOnboarding}>
                      {source.name} {!source.enabledForRutOnboarding ? "(alta no habilitada)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                type="file"
                label="PDF del RUT DIAN"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/pdf,.pdf" }}
                onChange={e => updateRutFile("rutFile", e.target.files?.[0])}
                helperText={rutOnboardingForm.rutFile ? rutOnboardingForm.rutFile.name : "PDF legible, sin clave, maximo 8 MB."}
              />
            </Grid>
            {rutOnboardingDraftRestored && <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 10, borderRadius: 10, borderColor: "#d69e2e" }}>
                <Typography variant="body2"><strong>Se restauró el último intento con error.</strong> Revise y corrija los datos necesarios. Por seguridad del navegador, debe adjuntar nuevamente RUT, certificado, logo y resoluciones.</Typography>
              </Paper>
            </Grid>}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Servicios a configurar</Typography>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.invoice} onChange={e => setRutOnboardingForm(prev => ({ ...prev, invoice: e.target.checked }))} color="primary" />} label="Facturacion electronica" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.payroll} onChange={e => setRutOnboardingForm(prev => ({ ...prev, payroll: e.target.checked }))} color="primary" />} label="Nomina electronica" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.eqdocs} onChange={e => setRutOnboardingForm(prev => ({ ...prev, eqdocs: e.target.checked }))} color="primary" />} label="Documento soporte / equivalentes" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Clasificación de software</InputLabel>
                <Select
                  label="Clasificación de software"
                  value={rutOnboardingForm.softwareClassificationCode}
                  onChange={e => setRutOnboardingForm(prev => ({ ...prev, softwareClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {catalogs.software.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Clasificación comercial</InputLabel>
                <Select
                  label="Clasificación comercial"
                  value={rutOnboardingForm.commercialClassificationCode}
                  onChange={e => setRutOnboardingForm(prev => ({ ...prev, commercialClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {catalogs.commercial.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 10 }}>Software DIAN</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={<RutHelpLabel label="Nombre visible del software" help="SOLUWHATS lo calcula tras validar el RUT: facilweb_ seguido del NIT sin dígito de verificación." />}
                      value={rutOnboardingForm.softwareName}
                      InputProps={{ readOnly: true }}
                      helperText="Se calcula como facilweb_ + NIT sin DV al validar el RUT."
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label={<RutHelpLabel label="Software ID DIAN" help="Pegue el UUID de la columna Id de DIAN. No es el nombre visible del software." />}
                      value={rutOnboardingForm.softwareId}
                      onChange={e => setRutOnboardingForm(prev => ({ ...prev, softwareId: e.target.value.trim() }))}
                      placeholder="UUID de la columna Id de DIAN"
                      helperText="Pegue el UUID tecnico mostrado por DIAN. No es el nombre del software."
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={<RutHelpLabel label="PIN del software" help="Clave corta configurada para el software en DIAN. El valor inicial recomendado es 12345." />}
                      value={rutOnboardingForm.softwarePin}
                      onChange={e => setRutOnboardingForm(prev => ({ ...prev, softwarePin: e.target.value }))}
                      helperText="Por defecto: 12345"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" type="file" label="Certificado digital (opcional)" InputLabelProps={{ shrink: true }} inputProps={{ accept: ".p12,.pfx,application/x-pkcs12" }} onChange={e => updateRutFile("certificateFile", e.target.files?.[0])} helperText={rutOnboardingForm.certificateFile ? rutOnboardingForm.certificateFile.name : "P12 o PFX"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" type="password" label="Clave del certificado" value={rutOnboardingForm.certificatePassword} onChange={e => setRutOnboardingForm(prev => ({ ...prev, certificatePassword: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" type="file" label="Logo (opcional)" InputLabelProps={{ shrink: true }} inputProps={{ accept: "image/png,image/jpeg" }} onChange={e => updateRutFile("logoFile", e.target.files?.[0])} helperText={rutOnboardingForm.logoFile ? rutOnboardingForm.logoFile.name : "PNG o JPG, maximo 2 MB"} />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
                  <Box>
                    <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Resoluciones DIAN (PDF)</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Adjunte factura electronica, documento soporte u otras resoluciones. Solo PDF DIAN legible, sin clave ni imagen escaneada.
                    </Typography>
                  </Box>
                  <Button component="label" size="small" color="primary" variant="outlined" startIcon={<AddIcon />}>
                    Adjuntar PDF
                    <input hidden type="file" accept="application/pdf,.pdf" onChange={e => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(file => updateRutFile("resolutionFiles", file));
                      e.target.value = "";
                    }} multiple />
                  </Button>
                </Box>
                {rutOnboardingForm.resolutionFiles.length > 0 && (
                  <Box mt={1} display="flex" flexWrap="wrap" gridGap={8}>
                    {rutOnboardingForm.resolutionFiles.map((item, index) => (
                      <Chip
                        key={`${item.file.name}-${item.file.lastModified}`}
                        label={`${item.file.name} (${item.pages} pag.)`}
                        onDelete={() => removeResolutionFile(index)}
                        deleteIcon={<DeleteOutlineIcon />}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                  El endpoint actual recibe los datos estructurados de cada resolucion. Estos PDF quedan validados y listos para adjuntar cuando la API publique su campo de recepcion.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => setRutOnboardingForm(prev => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
              >
                {rutOnboardingForm.showAdvanced ? "Ocultar opciones tecnicas" : "Mostrar opciones tecnicas y pruebas"}
              </Button>
            </Grid>
            {rutOnboardingForm.showAdvanced && <>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.setupEnvironment} onChange={e => setRutOnboardingForm(prev => ({ ...prev, setupEnvironment: e.target.checked }))} color="primary" />} label="Preparar ambiente de habilitacion" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.setupTestResolutions} onChange={e => setRutOnboardingForm(prev => ({ ...prev, setupTestResolutions: e.target.checked }))} color="primary" />} label="Crear resoluciones estandar de prueba" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Pruebas DIAN (solo bajo solicitud expresa)</Typography>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.runInvoiceTests} onChange={e => setRutOnboardingForm(prev => ({ ...prev, runInvoiceTests: e.target.checked }))} color="primary" />} label="Ejecutar pruebas de factura" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.runPayrollTests} onChange={e => setRutOnboardingForm(prev => ({ ...prev, runPayrollTests: e.target.checked }))} color="primary" />} label="Ejecutar pruebas de nomina" />
            </Grid>
            {rutOnboardingForm.runInvoiceTests && <>
              <Grid item xs={12} md={6}><TextField required fullWidth variant="outlined" label={<RutHelpLabel label="Set de pruebas factura DIAN" help="UUID que DIAN entrega para ejecutar el set de pruebas de factura de esta empresa." />} value={rutOnboardingForm.invoiceTestSetId} onChange={e => setRutOnboardingForm(prev => ({ ...prev, invoiceTestSetId: e.target.value }))} helperText="Obligatorio para ejecutar las pruebas de factura." /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" variant="outlined" label="Consecutivo factura" value={rutOnboardingForm.invoiceTestStartNumber} onChange={e => setRutOnboardingForm(prev => ({ ...prev, invoiceTestStartNumber: e.target.value }))} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" variant="outlined" label="Consecutivo NC" value={rutOnboardingForm.creditNoteTestNumber} onChange={e => setRutOnboardingForm(prev => ({ ...prev, creditNoteTestNumber: e.target.value }))} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" variant="outlined" label="Consecutivo ND" value={rutOnboardingForm.debitNoteTestNumber} onChange={e => setRutOnboardingForm(prev => ({ ...prev, debitNoteTestNumber: e.target.value }))} /></Grid>
            </>}
            {rutOnboardingForm.runPayrollTests && <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Set de pruebas nomina DIAN" value={rutOnboardingForm.payrollTestSetId} onChange={e => setRutOnboardingForm(prev => ({ ...prev, payrollTestSetId: e.target.value }))} /></Grid>}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.completeExisting} onChange={e => setRutOnboardingForm(prev => ({ ...prev, completeExisting: e.target.checked }))} color="primary" />} label="Completar empresa existente solo si sigue en habilitacion" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.updateExisting} onChange={e => setRutOnboardingForm(prev => ({ ...prev, updateExisting: e.target.checked }))} color="primary" />} label="Permitir actualizar empresa existente no productiva" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth multiline minRows={3} variant="outlined" label="Correcciones manuales JSON (opcional)" value={rutOnboardingForm.overridesJson} onChange={e => setRutOnboardingForm(prev => ({ ...prev, overridesJson: e.target.value }))} placeholder={'{"email":"correo@empresa.com","phone":"3000000000"}'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth multiline minRows={3} variant="outlined" label="Resoluciones personalizadas JSON (opcional)" value={rutOnboardingForm.resolutionsJson} onChange={e => setRutOnboardingForm(prev => ({ ...prev, resolutionsJson: e.target.value }))} placeholder={'[{"type_document_id":1,"prefix":"FV","resolution":"..."}]'} />
            </Grid>
            </>}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.confirmCreate} onChange={e => setRutOnboardingForm(prev => ({ ...prev, confirmCreate: e.target.checked }))} color="secondary" />} label="Confirmo que deseo crear o completar esta empresa en la API seleccionada." />
            </Grid>
            {rutOnboardingResult && (
              <Grid item xs={12}>
                <Paper variant="outlined" style={{ padding: 16, maxHeight: 310, overflow: "auto" }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>Resultado de la API</Typography>
                  {getRutMissingFields(rutOnboardingResult).length > 0 && (
                    <Box mb={1.5} p={1.5} borderRadius={12} bgcolor="rgba(244, 67, 54, 0.10)" border="1px solid rgba(244, 67, 54, 0.35)">
                      <Typography variant="body2" style={{ fontWeight: 800, color: "#f44336" }}>
                        Faltan datos obligatorios para crear: {formatRutMissingFields(rutOnboardingResult)}.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Puede completarlos en Correcciones manuales JSON y volver a intentar sin cambiar el PDF original.
                      </Typography>
                    </Box>
                  )}
                  <Typography component="pre" variant="caption" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{JSON.stringify(rutOnboardingResult, null, 2)}</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRutOnboardingOpen(false)} disabled={rutOnboardingSaving}>Cerrar</Button>
          <Button onClick={() => setRutOnboardingHelpOpen(true)} disabled={rutOnboardingSaving}>Ayuda</Button>
          <Button onClick={clearRutOnboarding} disabled={rutOnboardingSaving}>Limpiar</Button>
          <Button color="primary" variant="outlined" onClick={() => submitRutOnboarding(false)} disabled={rutOnboardingSaving}>{rutOnboardingSaving ? "Procesando..." : "Validar RUT"}</Button>
          <Button color="secondary" variant="contained" onClick={() => submitRutOnboarding(true)} disabled={rutOnboardingSaving || !rutOnboardingResult}>Crear en API</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rutOnboardingHelpOpen} onClose={() => setRutOnboardingHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Guía rápida: alta de empresa por RUT</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph><strong>1. Seleccione la API destino.</strong> Es el servidor donde se creará o completará la empresa.</Typography>
          <Typography paragraph><strong>2. Adjunte y valide el RUT.</strong> Esta primera acción solo lee los datos; no crea nada.</Typography>
          <Typography paragraph><strong>3. Revise el nombre del software y pegue el Software ID DIAN.</strong> El nombre se completa automáticamente. El ID es el UUID de la columna <em>Id</em> de DIAN.</Typography>
          <Typography paragraph><strong>4. Adjunte certificado, logo y resoluciones si aplican.</strong> Las resoluciones PDF se revisan localmente antes del envío.</Typography>
          <Typography paragraph><strong>5. Use opciones técnicas solo si aplica.</strong> Para pruebas de factura son obligatorios Software ID y Set de pruebas DIAN. Use consecutivos nuevos al repetir pruebas.</Typography>
          <Typography paragraph><strong>6. Cree o complete.</strong> Marque la confirmación y use Crear en API. Si la empresa está en habilitación puede completarse; producción bloquea actualizaciones.</Typography>
          <Typography variant="caption" color="textSecondary">Si la API responde un error, el formulario guarda los datos escritos para corregirlos luego. Los archivos se deben adjuntar otra vez por seguridad del navegador.</Typography>
        </DialogContent>
        <DialogActions><Button color="primary" onClick={() => setRutOnboardingHelpOpen(false)}>Entendido</Button></DialogActions>
      </Dialog>

      <Dialog open={clientModalOpen} onClose={() => setClientModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{clientForm.id ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre"
                value={clientForm.name}
                onChange={e => setClientForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="NIT / Cedula"
                value={clientForm.identificationNumber}
                onChange={e => setClientForm(prev => ({ ...prev, identificationNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Codigo historico del agente"
                value={clientForm.installationCode}
                onChange={e => setClientForm(prev => ({ ...prev, installationCode: e.target.value }))}
                helperText="Codigo heredado para agentes ya instalados."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="DV"
                value={clientForm.dv}
                onChange={e => setClientForm(prev => ({ ...prev, dv: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                label="Correo principal"
                value={clientForm.email}
                onChange={e => setClientForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                label="Telefono principal"
                value={clientForm.phone}
                onChange={e => setClientForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Direccion"
                value={clientForm.address}
                onChange={e => setClientForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Departamento / Estado"
                value={clientForm.stateName}
                onChange={e => setClientForm(prev => ({ ...prev, stateName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Matricula mercantil"
                value={clientForm.merchantRegistration}
                onChange={e => setClientForm(prev => ({ ...prev, merchantRegistration: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={String(clientForm.isActive)}
                  onChange={e => setClientForm(prev => ({ ...prev, isActive: e.target.value === "true" }))}
                >
                  <MenuItem value="true">Activo</MenuItem>
                  <MenuItem value="false">Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Software</InputLabel>
                <Select
                  label="Software"
                  value={clientForm.softwareClassificationCode}
                  onChange={e => setClientForm(prev => ({ ...prev, softwareClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Sin definir</MenuItem>
                  {catalogs.software.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Clasificacion comercial</InputLabel>
                <Select
                  label="Clasificacion comercial"
                  value={clientForm.commercialClassificationCode}
                  onChange={e => setClientForm(prev => ({ ...prev, commercialClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Sin definir</MenuItem>
                  {catalogs.commercial.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {Object.entries(CLIENT_EDITABLE_CATALOG_LABELS).map(([catalogType, label]) => (
              <Grid item xs={12} md={6} key={catalogType}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{label}</InputLabel>
                  <Select
                    label={label}
                    value={String(clientForm.catalogSelections?.[catalogType]?.code || "")}
                    onChange={e => handleEditableCatalogChange(catalogType, e.target.value)}
                  >
                    <MenuItem value="">Sin definir</MenuItem>
                    {getAttributeOptions(catalogType).map(option => (
                      <MenuItem
                        key={`${catalogType}-${option.code || option.name}`}
                        value={String(option.code || option.name || "")}
                      >
                        {option.name || option.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Telefono de notificacion"
                value={clientForm.primaryNotificationPhone}
                onChange={e => setClientForm(prev => ({ ...prev, primaryNotificationPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Correo de notificacion"
                value={clientForm.primaryNotificationEmail}
                onChange={e => setClientForm(prev => ({ ...prev, primaryNotificationEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                variant="outlined"
                label="Observaciones"
                value={clientForm.notes}
                onChange={e => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientModalOpen(false)} disabled={clientSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClient} color="primary" variant="contained" disabled={clientSaving}>
            {clientSaving ? "Guardando..." : clientForm.id ? "Guardar cambios" : "Crear cliente"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(clientStatusConfirmation)}
        onClose={() => !clientSaving && setClientStatusConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar cambio de estado</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            Va a cambiar el estado del cliente en SOLUWHATS y en las APIs reales donde aparezca registrado.
          </Typography>
          <Box className={classes.detailSection}>
            <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
              {clientStatusConfirmation?.payload?.name || "Cliente"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              NIT/Cedula: {clientStatusConfirmation?.payload?.identificationNumber || "Sin dato"}
              {clientStatusConfirmation?.payload?.dv ? `-${clientStatusConfirmation.payload.dv}` : ""}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Estado: {clientStatusConfirmation?.from || "Sin dato"} a {clientStatusConfirmation?.to || "Sin dato"}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            Si el cliente aparece en varias APIs, SOLUWHATS intentara aplicar el mismo estado en todas. La operacion quedara registrada con el usuario y la respuesta de cada API.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientStatusConfirmation(null)} disabled={clientSaving}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={executeSaveClientWithApiStatus} disabled={clientSaving}>
            {clientSaving ? "Aplicando..." : "Confirmar y aplicar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={apiTokensOpen}
        onClose={() => !apiTokensLoading && setApiTokensOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tokens de la empresa</DialogTitle>
        <DialogContent dividers>
          {apiTokensLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
          ) : (
            <>
              <Box className={classes.detailSection} mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                  {apiTokensCustomer?.name || "Cliente"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  NIT/Cedula: {apiTokensCustomer?.identificationNumber || "Sin dato"}
                  {apiTokensCustomer?.dv ? `-${apiTokensCustomer.dv}` : ""}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                  Se muestra un token por cada API activa donde aparece esta empresa. Las credenciales internas de conexion de SoluWhats no se muestran aqui.
                </Typography>
              </Box>

              {(apiTokensData?.matches || []).length ? (
                <Box className={classes.compactList}>
                  {apiTokensData.matches.map(match => (
                    <Box key={`${match.sourceId}-${match.sourceKey}`} className={classes.compactRow}>
                      <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                        {match.sourceName || match.sourceCode || "API"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                        {match.company?.name || "Empresa encontrada"}
                      </Typography>
                      {match.apiToken ? (
                        <Box display="flex" alignItems="center" gridGap={8} mt={1.5}>
                          <TextField
                            fullWidth
                            value={match.apiToken}
                            variant="outlined"
                            size="small"
                            InputProps={{ readOnly: true }}
                            inputProps={{ "aria-label": `Token de ${match.sourceName || match.sourceCode || "API"}` }}
                          />
                          <Tooltip title="Copiar token">
                            <IconButton color="primary" aria-label="Copiar token" onClick={() => copyApiToken(match.apiToken)}>
                              <FileCopyIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
                          Esta API encontro la empresa, pero no devolvio un token para ella.
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {apiTokensData?.message || "No se encontro esta empresa en las APIs activas."}
                </Typography>
              )}

              {(apiTokensData?.errors || []).length ? (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="error">APIs que no pudieron consultarse</Typography>
                  {apiTokensData.errors.map(error => (
                    <Typography key={`${error.sourceId}-${error.sourceKey}`} variant="body2" color="textSecondary">
                      {error.sourceName || error.sourceCode}: {error.message}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiTokensOpen(false)} color="primary" disabled={apiTokensLoading}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detailRecord ? `${detailRecord.name} - ${detailRecord.identificationNumber}` : "Detalle del cliente"}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : detailRecord ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.detailSection}>
                  <Grid container spacing={2}>
                    {[
                      ["Contactos vinculados", detailRecord.metrics?.contacts || 0],
                      ["Establecimientos", detailRecord.metrics?.establishments || 0],
                      ["Tickets", detailRecord.metrics?.tickets || 0],
                      ["Tareas", detailRecord.metrics?.tasks || 0]
                    ].map(([label, value]) => (
                      <Grid item xs={6} md={3} key={label}>
                        <Box className={classes.detailMetric}>
                          <Typography variant="overline">{label}</Typography>
                          <Typography variant="h4" style={{ fontWeight: 800, marginTop: 6 }}>
                            {value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
                    Datos principales
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Correo</Typography>
                      <Typography variant="body2">{detailRecord.email || "Sin correo"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Telefono</Typography>
                      <Typography variant="body2">{detailRecord.phone || "Sin telefono"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Notificacion</Typography>
                      <Typography variant="body2">
                        {detailRecord.primaryNotificationPhone || detailRecord.primaryNotificationEmail || "Sin dato"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Origen</Typography>
                      <Typography variant="body2">{detailRecord.originCode || "manual"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Codigo historico</Typography>
                      <Typography variant="body2">{detailRecord.installationCode || "Sin codigo"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Direccion</Typography>
                      <Typography variant="body2">{detailRecord.address || "Sin direccion"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Observaciones</Typography>
                      <Typography variant="body2">{detailRecord.notes || "Sin observaciones"}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
                    Planes y catalogos importados
                  </Typography>
                  <Box className={classes.compactList}>
                    {(detailRecord.plans || []).length ? detailRecord.plans.map(plan => (
                      <Box key={plan.id} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {plan.planType ? `Plan ${String(plan.planType).toUpperCase()}` : "Plan importado"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {plan.planName || "Sin nombre"}{plan.documents ? ` - ${plan.documents} folios/documentos` : ""}{` - inicio ${formatDisplayDate(plan.startDate)}`}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="textSecondary">Sin planes importados.</Typography>
                    )}
                    {(detailRecord.catalogValues || []).length ? detailRecord.catalogValues.slice(0, 6).map(item => (
                      <Chip
                        key={item.id}
                        size="small"
                        label={`${CLIENT_CATALOG_LABELS[item.catalogType] || item.catalogType}: ${item.catalogName || item.catalogCode}`}
                      />
                    )) : null}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="h6" style={{ fontWeight: 800 }}>
                      Establecimientos
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={openCreateEstablishmentModal}
                    >
                      Nuevo
                    </Button>
                  </Box>
                  <Box className={classes.compactList}>
                    {(detailRecord.establishments || []).length ? detailRecord.establishments.map(item => (
                      <Box key={item.id} className={classes.compactRow}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {item.name || "Establecimiento sin nombre"}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.isActive === false ? "Inactivo" : "Activo"}
                            color={item.isActive === false ? "default" : "primary"}
                          />
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {item.primaryPhone || "Sin telefono"} - {item.primaryEmail || "Sin correo"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Codigo: {item.installationCode || "Sin codigo"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.address || "Sin direccion"}
                        </Typography>
                        <Box mt={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => openEditEstablishmentModal(item)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<DeleteOutlineIcon />}
                            style={{ marginLeft: 8 }}
                            onClick={() => handleDeleteEstablishment(item)}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="textSecondary">Sin establecimientos asociados.</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="h6" style={{ fontWeight: 800 }}>
                      Contactos relacionados
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={handleLinkPossibleContacts}
                    >
                      Relacionar posibles
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      style={{ marginLeft: 8 }}
                      onClick={openContactLinkDialog}
                    >
                      Agregar contactos
                    </Button>
                  </Box>
                  <Box className={classes.compactList}>
                    {(detailRecord.contacts || []).length ? detailRecord.contacts.slice(0, 8).map(item => (
                      <Box key={item.id} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {item.name || "Sin nombre"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.number || "Sin numero"} - {item.email || "Sin correo"}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="textSecondary">Sin contactos vinculados.</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
                    Tickets y tareas recientes
                  </Typography>
                  <Box className={classes.compactList}>
                    {(detailRecord.tickets || []).slice(0, 5).map(item => (
                      <Box key={`ticket-${item.id}`} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          Ticket #{item.id} - {item.contact?.name || "Sin contacto"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.queue?.name || "Sin cola"} - {item.user?.name || "Sin usuario"} - {item.status}
                        </Typography>
                      </Box>
                    ))}
                    {(detailRecord.operationalTasks || []).slice(0, 5).map(item => (
                      <Box key={`task-${item.id}`} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.assignedUser?.name || "Sin responsable"} - {item.priority} - {item.status}
                        </Typography>
                      </Box>
                    ))}
                    {!(detailRecord.tickets || []).length && !(detailRecord.operationalTasks || []).length ? (
                      <Typography variant="body2" color="textSecondary">
                        Sin tickets ni tareas relacionadas.
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resolutionDialogOpen}
        onClose={() => !resolutionProcessing && setResolutionDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Subir resolucion DIAN
          <Box component="span" ml={1.5}>
            <Tooltip title="Ayuda del proceso" arrow>
              <IconButton size="small" onClick={() => setResolutionHelpOpen(true)}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {resolutionLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.detailSection}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                        {resolutionCustomer?.name || "Cliente"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        NIT/Cedula: {resolutionCustomer?.identificationNumber || "Sin dato"}
                        {resolutionCustomer?.dv ? `-${resolutionCustomer.dv}` : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary">Procesamiento</Typography>
                      <Typography variant="body2">
                        El endpoint identifica automaticamente la API y el software tecnico.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="textSecondary">Software ID</Typography>
                      <Typography variant="body2">Se consulta internamente</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="file"
                  label="PDF oficial de resolucion DIAN"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: "application/pdf,.pdf" }}
                  onChange={handleResolutionFileChange}
                  helperText={resolutionFile ? `Seleccionado: ${resolutionFile.name}` : "PDF legible, sin clave, maximo 8 MB."}
                />
              </Grid>
              {canSyncResolutionToNube(resolutionCustomer) ? (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncResolutionToNube}
                        onChange={event => setSyncResolutionToNube(event.target.checked)}
                        color="primary"
                      />
                    }
                    label="Subir tambien a FacilWeb Nube"
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    Primero se procesa la resolucion DIAN. Despues se valida en FacilWeb Nube y solo se aplica con su confirmacion.
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  style={{ height: "100%", minHeight: 54 }}
                  disabled={resolutionProcessing || !resolutionFile}
                  onClick={processResolution}
                >
                  {resolutionProcessing ? "Procesando..." : "Procesar resolucion"}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800, marginTop: 8, marginBottom: 10 }}>
                  Historial de resoluciones
                </Typography>
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Resolucion</TableCell>
                        <TableCell>Prefijo</TableCell>
                        <TableCell>Documento</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Detalle</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resolutionHistory.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.updatedAt ? new Date(item.updatedAt).toLocaleString("es-CO") : "Sin fecha"}</TableCell>
                          <TableCell>{item.uploadedByUser?.name || "Sistema"}</TableCell>
                          <TableCell>{item.resolutionNumber || "Pendiente"}</TableCell>
                          <TableCell>{item.prefix || "-"}</TableCell>
                          <TableCell>{item.resolutionType || "Resolucion DIAN"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={item.sourceStatus || "pending"} color={item.sourceStatus === "success" ? "primary" : "default"} />
                          </TableCell>
                          <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                            <Button size="small" variant="outlined" onClick={() => openResolutionDetail(item)}>Ver</Button>
                            {item.retryRecommended || item.sourceStatus === "pending_dian" ? (
                              <Button size="small" color="primary" disabled={resolutionProcessing} onClick={() => retryResolution(item)}>
                                Reintentar
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!resolutionHistory.length ? (
                        <TableRow><TableCell colSpan={7} align="center">Aun no hay resoluciones procesadas para este cliente.</TableCell></TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </div>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionHelpOpen(true)}>Ayuda</Button>
          <Button onClick={() => setResolutionDialogOpen(false)} disabled={resolutionProcessing}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(nubeSyncConfirmation)}
        onClose={() => !nubeSyncApplying && setNubeSyncConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar sincronizacion con FacilWeb Nube</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            {nubeSyncConfirmation?.preview?.message || "La resolucion esta lista para sincronizarse con FacilWeb Nube."}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta accion aplicara la resolucion en FacilWeb Nube. No se enviara ningun token al navegador ni se ejecutara automaticamente sin su confirmacion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNubeSyncConfirmation(null)} disabled={nubeSyncApplying}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={applyNubeResolutionSync} disabled={nubeSyncApplying}>
            {nubeSyncApplying ? "Sincronizando..." : "Confirmar y sincronizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolutionHelpOpen} onClose={() => setResolutionHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ayuda: subir resolucion DIAN</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>1. Seleccione el PDF oficial de la resolucion DIAN. No escriba NIT, DV, prefijo, numero de resolucion ni Software ID.</Typography>
          <Typography paragraph>2. Presione Procesar resolucion. SOLUWHATS envia solamente el PDF; el endpoint identifica el cliente, la API y el Software ID tecnico.</Typography>
          <Typography paragraph>3. Si DIAN aun no ha asociado la resolucion al software, el registro quedara pendiente y podra usar Reintentar sin volver a cargar el PDF. Esto no es un error definitivo.</Typography>
          <Typography paragraph>4. Para clientes clasificados como FACILWEB NUBE, puede activar Subir tambien a FacilWeb Nube. El sistema primero hace una validacion sin cambios. Si la informacion esta lista, le pedira confirmacion antes de crear o reemplazar una resolucion en Nube.</Typography>
          <Typography paragraph>En el flujo normal no se abre Chrome ni se manipula el certificado. En un escenario especial futuro, el sistema podria abrir una ventana asistida de DIAN. Si aparece, no la cierre ni la manipule sin indicacion; puede minimizarla mientras termina. Si se solicita una accion manual, siga las instrucciones visibles, como resolver un CAPTCHA o confirmar una asociacion.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setResolutionHelpOpen(false)}>Entendido</Button></DialogActions>
      </Dialog>

      <Dialog open={resolutionDetailOpen} onClose={() => setResolutionDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle del procesamiento de resolucion</DialogTitle>
        <DialogContent dividers>
          {resolutionDetail ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{resolutionDetail.resolution?.visibleMessage || "Sin mensaje"}</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, overflowWrap: "anywhere" }}>
                  Archivo: {resolutionDetail.resolution?.fileName || "PDF no disponible"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Datos extraidos</Typography>
                <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify({
                  identificacion: resolutionDetail.resolution?.extractedIdentificationNumber,
                  dv: resolutionDetail.resolution?.extractedDv,
                  resolucion: resolutionDetail.resolution?.resolutionNumber,
                  prefijo: resolutionDetail.resolution?.prefix,
                  rangoDesde: resolutionDetail.resolution?.rangeFrom,
                  rangoHasta: resolutionDetail.resolution?.rangeTo,
                  tipoDocumento: resolutionDetail.resolution?.resolutionType,
                  operacion: resolutionDetail.resolution?.sourceOperation,
                  httpStatus: resolutionDetail.resolution?.sourceHttpStatus
                }, null, 2)}</pre>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Respuesta de la API</Typography>
                <pre style={{ maxHeight: 260, overflow: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify(resolutionDetail.resolution?.sourceResponsePayloadJson || {}, null, 2)}</pre>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Historial de intentos</Typography>
                {(resolutionDetail.events || []).map(event => (
                  <Box key={event.id} className={classes.compactRow} mt={1}>
                    <Typography variant="subtitle2">{event.eventType} - {event.performedByUser?.name || "Sistema"}</Typography>
                    <Typography variant="body2">{event.details}</Typography>
                    <Typography variant="caption" color="textSecondary">{event.eventAt ? new Date(event.eventAt).toLocaleString("es-CO") : ""}</Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          ) : <Box display="flex" justifyContent="center" py={5}><CircularProgress size={28} /></Box>}
        </DialogContent>
        <DialogActions><Button onClick={() => setResolutionDetailOpen(false)}>Cerrar</Button></DialogActions>
      </Dialog>

      <Dialog
        open={apiPrefixesOpen}
        onClose={() => !apiPrefixesLoading && !resolutionAssignmentSaving && setApiPrefixesOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Prefijos y sucursales</DialogTitle>
        <DialogContent dividers>
          {apiPrefixesLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                    {apiPrefixesCustomer?.name || "Cliente"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    NIT/Cedula: {apiPrefixesCustomer?.identificationNumber || "Sin dato"}
                    {apiPrefixesCustomer?.dv ? `-${apiPrefixesCustomer.dv}` : ""}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                    Asigne la sucursal. Use los tres puntos de cada fila para ver rangos, vigencia y otras acciones.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>API a revisar</InputLabel>
                  <Select
                    label="API a revisar"
                    value={apiPrefixesSourceId}
                    disabled={resolutionAssignmentSaving}
                    onChange={handleApiPrefixesSourceChange}
                  >
                    {(apiPrefixesStatus?.sources || apiPrefixesStatus?.matches || []).map(match => (
                      <MenuItem key={match.sourceId} value={String(match.sourceId)} disabled={match.status === "not_found"}>
                        {match.sourceName || match.sourceCode}{match.status === "not_found" ? " - Empresa no encontrada" : match.status === "error" ? " - Consulta fallida (reintentar)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                {(apiPrefixesStatus?.errors || []).map(item => <Typography key={item.sourceId} variant="caption" color="error" display="block">No se pudo verificar {item.sourceName || item.sourceCode}. No se aplicaran cambios alli sin confirmar la empresa.</Typography>)}
              </Grid>

              <Grid item xs={12}>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openApiPrefixEditor()}
                  disabled={!apiPrefixesSourceId || apiPrefixesLoading || resolutionAssignmentSaving}
                >
                  Nuevo prefijo manual
                </Button>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                  Los datos fiscales DIAN permanecen protegidos. Las asignaciones no activan cobros ni bloqueos.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {apiPrefixRows.length ? (
                  <CustomerPrefixesTable rows={apiPrefixRows} assignments={resolutionAssignments}
                    saving={resolutionAssignmentSaving} onAssign={saveResolutionEstablishment}
                    onManageEstablishments={() => { setApiPrefixesOpen(false); openDetailModal(apiPrefixesCustomer.id); }}
                    onEdit={openApiPrefixEditor} onCopy={previewResolutionCopy}
                    formatNumber={formatPlanNumber} formatDate={formatPlanDate}
                    typeColor={getApiPrefixTypeColor} canCopy={(apiPrefixesStatus?.matches || []).length > 1} />
                ) : <Typography variant="body2" color="textSecondary">No hay prefijos o resoluciones disponibles en esta API.</Typography>}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleApiPrefixesSourceChange({ target: { value: apiPrefixesSourceId } })}
            disabled={apiPrefixesLoading || resolutionAssignmentSaving}
          >
            Actualizar
          </Button>
          <Button onClick={() => setApiPrefixesOpen(false)} disabled={apiPrefixesLoading || resolutionAssignmentSaving}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resolutionCopy)} onClose={() => !resolutionCopy?.busy && setResolutionCopy(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Aplicar sucursal en otras APIs</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">Establecimiento: <strong>{resolutionCopy?.branchName}</strong></Typography>
          <Typography variant="body2" color="textSecondary">Solo se copia la asignacion local a resoluciones con igual tipo, prefijo, numero, rango y vigencia. No se crean resoluciones ni se sobrescriben otras sucursales.</Typography>
          {resolutionCopy?.busy && <Box py={2}><CircularProgress size={24} /><Typography variant="caption"> Consultando / guardando, espere por favor.</Typography></Box>}
          {(resolutionCopy?.rows || []).map(item => <Box key={item.sourceId} mt={2}><Typography variant="body2" style={{ fontWeight: 700 }}>{item.name}</Typography><Typography variant="body2">{item.message}</Typography></Box>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionCopy(null)} disabled={resolutionCopy?.busy}>Cerrar</Button>
          {!resolutionCopy?.done && <Button color="primary" variant="contained" disabled={resolutionCopy?.busy || !resolutionCopy?.rows.some(item => item.ready)} onClick={applyResolutionCopy}>Confirmar aplicacion</Button>}
        </DialogActions>
      </Dialog>

      <Dialog
        open={apiPrefixEditorOpen}
        onClose={() => !apiPrefixSaving && setApiPrefixEditorOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{apiPrefixForm.id ? "Editar prefijo manual" : "Nuevo prefijo manual"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className={classes.detailSection}>
                <Typography variant="body2">
                  API seleccionada: {apiPrefixesData?.source?.name || apiPrefixesData?.source?.code || "Sin API"}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                  Esta accion solo aplica para prefijos manuales. No modifica resoluciones DIAN ni llaves tecnicas.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo de documento</InputLabel>
                <Select
                  label="Tipo de documento"
                  value={apiPrefixForm.type_document_id}
                  onChange={event => setApiPrefixForm(prev => ({ ...prev, type_document_id: event.target.value }))}
                >
                  {apiPrefixTypeOptions.map(option => (
                    <MenuItem key={option.id} value={String(option.id)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Prefijo"
                value={apiPrefixForm.prefix}
                onChange={event => setApiPrefixForm(prev => ({
                  ...prev,
                  prefix: String(event.target.value || "").toUpperCase()
                }))}
                inputProps={{ maxLength: 4 }}
                helperText="Maximo 4 caracteres, letras o numeros."
              />
            </Grid>
            {process.env.REACT_APP_CUSTOMER_SOFTWARE_DEV === 'true' && [4, 5].includes(Number(apiPrefixForm.type_document_id)) && <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Numero de resolucion interna (opcional)"
                disabled={apiPrefixesData?.result?.rules?.internal_resolution_supported !== true}
                value={apiPrefixForm.resolution || ''} placeholder="Ejemplo: 1800000010"
                onChange={event => setApiPrefixForm(prev => ({ ...prev, resolution: event.target.value }))}
                inputProps={{ inputMode: 'numeric', maxLength: 20 }}
                helperText={apiPrefixesData?.result?.rules?.internal_resolution_supported === true
                  ? "Identificador interno para NC o ND. No corresponde a una autorizacion DIAN. No modifica documentos ya emitidos."
                  : "Esta API todavia no ofrece el numero interno. Las demas opciones de prefijos conservan su funcionamiento."} />
            </Grid>}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Desde"
                type="number"
                value={apiPrefixForm.from}
                onChange={event => setApiPrefixForm(prev => ({ ...prev, from: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Hasta"
                type="number"
                value={apiPrefixForm.to}
                onChange={event => setApiPrefixForm(prev => ({ ...prev, to: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Fecha inicial"
                type="date"
                value={apiPrefixForm.date_from}
                onChange={event => setApiPrefixForm(prev => ({ ...prev, date_from: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Fecha final"
                type="date"
                value={apiPrefixForm.date_to}
                onChange={event => setApiPrefixForm(prev => ({ ...prev, date_to: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiPrefixEditorOpen(false)} disabled={apiPrefixSaving}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={handleSaveApiPrefix} disabled={apiPrefixSaving}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(apiPrefixConfirmation)}
        onClose={() => !apiPrefixSaving && setApiPrefixConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar prefijo manual</DialogTitle>
        <DialogContent dividers>
          <Box className={classes.detailSection}>
            <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
              {apiPrefixesCustomer?.name || "Cliente"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Prefijo: {String(apiPrefixForm.prefix || "").toUpperCase() || "Sin prefijo"}
            </Typography>
            {process.env.REACT_APP_CUSTOMER_SOFTWARE_DEV === 'true' && [4, 5].includes(Number(apiPrefixForm.type_document_id)) && <Typography variant="body2">
              Resolucion interna: {apiPrefixForm.resolution || 'Sin numero'}
            </Typography>}
            <Typography variant="body2" color="textSecondary">
              Rango: {apiPrefixForm.from || 0} a {apiPrefixForm.to || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              API: {apiPrefixesData?.source?.name || apiPrefixesData?.source?.code || "API seleccionada"}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            {apiPrefixConfirmation?.detail || "Confirme solo si desea guardar este prefijo manual en la API seleccionada."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiPrefixConfirmation(null)} disabled={apiPrefixSaving}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={executeSaveApiPrefix} disabled={apiPrefixSaving}>
            {apiPrefixSaving ? "Guardando..." : "Confirmar y guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={apiOperationsOpen}
        onClose={() => !apiOperationsSaving && setApiOperationsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Ambiente real y planes</DialogTitle>
        <DialogContent dividers>
          {apiOperationsLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.detailSection}>
                  <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                    {apiOperationsCustomer?.name || "Cliente"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    NIT/Cedula: {apiOperationsCustomer?.identificationNumber || "Sin dato"}
                    {apiOperationsCustomer?.dv ? `-${apiOperationsCustomer.dv}` : ""}
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {apiOperationsStatus?.message || "Estado operativo aun no consultado."}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                    Los ambientes se leen desde la API y los cambios se envian a la API con confirmacion. SoluWats queda sincronizado despues de cada operacion.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Tabs
                  value={apiOperationsTab}
                  onChange={(_event, value) => setApiOperationsTab(value)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Ambientes" />
                  <Tab label="Planes y folios" />
                  <Tab label="Historial de folios" />
                </Tabs>
              </Grid>

              {apiOperationsTab === 0 ? <>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800 }}>APIs donde aparece</Typography>
                {apiOperationsStatus?.selectedSourceName ? (
                  <Typography variant="body2" color="textSecondary">
                    API principal elegida: {apiOperationsStatus.selectedSourceName}. Si aparece en varias APIs, se usa el registro mas reciente.
                  </Typography>
                ) : null}
                <Box className={classes.compactList} mt={1}>
                  {(apiOperationsStatus?.matches || []).length ? apiOperationsStatus.matches.map(match => (
                    <Box
                      key={`${match.sourceId}-${match.sourceKey}`}
                      className={classes.compactRow}
                      style={String(match.sourceId) === String(apiOperationsStatus?.selectedSourceId)
                        ? {
                            borderColor: "rgba(64, 145, 213, 0.95)",
                            background: "rgba(64, 145, 213, 0.14)",
                            boxShadow: "0 0 0 1px rgba(64, 145, 213, 0.35) inset"
                          }
                        : undefined}
                    >
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        {match.sourceName || match.sourceCode}
                        {String(match.sourceId) === String(apiOperationsStatus?.selectedSourceId) ? " - API usada como referencia" : ""}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {match.sourceKey} - {match.company?.name || "Empresa encontrada"}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography variant="body2" color="textSecondary">No se encontro el cliente en APIs activas.</Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800 }}>Ambientes de la API real</Typography>
                <Typography variant="body2" color="textSecondary">
                  Use esto solo cuando quiera enviar el cambio a la API. Para editar solo el dato local, use Editar cliente.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Factura electronica</InputLabel>
                  <Select
                    label="Factura electronica"
                    value={apiEnvironmentForm.invoice_environment}
                    onChange={e => setApiEnvironmentForm(prev => ({ ...prev, invoice_environment: e.target.value }))}
                  >
                    <MenuItem value="">No cambiar ({environmentLabel(catalogEnvironmentValue(apiOperationsCustomer, "environment"))})</MenuItem>
                    <MenuItem value="habilitation">Habilitacion</MenuItem>
                    <MenuItem value="production">Produccion</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                  Documento soporte se sincroniza automaticamente con el ambiente de Factura electronica.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Nomina</InputLabel>
                  <Select
                    label="Nomina"
                    value={apiEnvironmentForm.payroll_environment}
                    onChange={e => setApiEnvironmentForm(prev => ({ ...prev, payroll_environment: e.target.value }))}
                  >
                    <MenuItem value="">No cambiar ({environmentLabel(catalogEnvironmentValue(apiOperationsCustomer, "payroll_environment"))})</MenuItem>
                    <MenuItem value="habilitation">Habilitacion</MenuItem>
                    <MenuItem value="production">Produccion</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveApiEnvironments}
                  disabled={apiOperationsSaving || !(apiOperationsStatus?.matches || []).length}
                >
                  {apiOperationsSaving ? "Guardando..." : "Guardar ambientes en API"}
                </Button>
              </Grid>
              </> : null}

              {apiOperationsTab === 1 ? <>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800, marginTop: 8 }}>Planes y folios</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  Use esta seccion para asignar o renovar planes y paquetes de folios.
                </Typography>
                {apiOperationsPlans?.error ? (
                  <Typography variant="body2" color="error">{apiOperationsPlans.error}</Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Servicio</InputLabel>
                  <Select
                    label="Servicio"
                    value={apiPlanForm.service}
                    onChange={e => setApiPlanForm(prev => ({
                      ...prev,
                      service: e.target.value,
                      plan_id: "",
                      absolut_plan_documents: ""
                    }))}
                  >
                    <MenuItem value="invoice">Factura electronica</MenuItem>
                    <MenuItem value="payroll">Nomina</MenuItem>
                    <MenuItem value="radian">RADIAN</MenuItem>
                    <MenuItem value="support_document">Documento soporte</MenuItem>
                    <MenuItem value="absolute">Paquete de folios</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!isAbsolutePlanService ? (
              <Grid item xs={12} md={7}>
                {apiSelectablePlans.length ? (
                  <Autocomplete
                    fullWidth
                    options={apiSelectablePlans}
                    value={apiSelectedPlanOption}
                    onChange={(_event, option) => setApiPlanForm(prev => ({
                      ...prev,
                      plan_id: option?.id ? String(option.id) : ""
                    }))}
                    getOptionLabel={option => option?.label || ""}
                    getOptionSelected={(option, value) => String(option?.id) === String(value?.id)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Plan"
                        helperText={apiSelectedPlanOption?.label || "Seleccione el plan por nombre; SOLUWHATS enviara internamente el ID."}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Plan ID"
                    value={apiPlanForm.plan_id}
                    onChange={e => setApiPlanForm(prev => ({ ...prev, plan_id: e.target.value }))}
                    helperText="La API no devolvio un catalogo de planes para armar el selector con busqueda."
                  />
                )}
              </Grid>
              ) : null}
              {isAbsolutePlanService ? (
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Paquete de folios"
                  value={apiPlanForm.absolut_plan_documents}
                  onChange={e => setApiPlanForm(prev => ({ ...prev, absolut_plan_documents: e.target.value }))}
                  helperText="Cantidad de folios a asignar."
                />
              </Grid>
              ) : null}
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={apiPlanForm.renew}
                      onChange={e => setApiPlanForm(prev => ({ ...prev, renew: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label={(
                    <Box component="span" display="inline-flex" alignItems="center" gridGap={4}>
                      Renovar
                      <Tooltip
                        arrow
                        placement="top"
                        title="Si esta activo, la API inicia un nuevo ciclo de folios desde hoy. Si esta apagado, intenta conservar la fecha anterior cuando aplica."
                      >
                        <HelpOutlineIcon style={{ fontSize: 16, cursor: "help", opacity: 0.8 }} />
                      </Tooltip>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Notas"
                  value={apiPlanForm.notes}
                  onChange={e => setApiPlanForm(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAssignApiPlan}
                  disabled={apiOperationsSaving || !(apiOperationsStatus?.matches || []).length}
                >
                  {apiOperationsSaving ? "Enviando..." : "Asignar plan o folios"}
                </Button>
              </Grid>
              </> : null}

              {apiOperationsTab === 2 ? <>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800, marginTop: 8 }}>Resumen actual de folios</Typography>
                {apiCurrentPlanRows.length ? (
                  <div className={classes.tableWrap} style={{ marginTop: 12, marginBottom: 20 }}>
                    <Table size="small">
                      <TableHead className={classes.tableHead}>
                        <TableRow>
                          <TableCell>Servicio</TableCell>
                          <TableCell>Plan actual</TableCell>
                          <TableCell>Inicio</TableCell>
                          <TableCell align="right">Incluidos</TableCell>
                          <TableCell align="right">Usados</TableCell>
                          <TableCell align="right">Disponibles</TableCell>
                          <TableCell>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apiCurrentPlanRows.map(row => (
                          <TableRow key={row.service}>
                            <TableCell>{row.serviceLabel}</TableCell>
                            <TableCell>{row.planName}</TableCell>
                            <TableCell>{formatPlanDate(row.startDate)}</TableCell>
                            <TableCell align="right">{formatPlanNumber(row.documents)}</TableCell>
                            <TableCell align="right">{formatPlanNumber(row.usedDocuments)}</TableCell>
                            <TableCell align="right">{formatPlanNumber(row.remainingDocuments)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={row.isExhausted === true ? "Consumido" : row.remainingDocuments === null || row.remainingDocuments === undefined ? "Sin limite" : "Disponible"}
                                color={row.isExhausted === true ? "secondary" : "primary"}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : null}
                {apiOperationsPlans?.error ? (
                  <Typography variant="body2" color="error">{apiOperationsPlans.error}</Typography>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800, marginTop: 8 }}>Historial de folios</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  Movimientos de asignacion o renovacion hechos desde SOLUWHATS.
                </Typography>
                {apiOperationsPlans?.error ? (
                  <Typography variant="body2" color="error">{apiOperationsPlans.error}</Typography>
                ) : apiPlanHistoryRows.length ? (
                  <div className={classes.tableWrap}>
                    <Table size="small">
                      <TableHead className={classes.tableHead}>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Servicio</TableCell>
                          <TableCell>Accion</TableCell>
                          <TableCell>Plan anterior</TableCell>
                          <TableCell>Plan nuevo</TableCell>
                          <TableCell align="right">Folios antes</TableCell>
                          <TableCell align="right">Folios despues</TableCell>
                          <TableCell>Notas</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apiPlanHistoryRows.map(row => (
                          <TableRow key={row.id}>
                            <TableCell>{formatPlanDate(row.date)}</TableCell>
                            <TableCell>{row.serviceLabel}</TableCell>
                            <TableCell>{row.action}</TableCell>
                            <TableCell>{row.previousPlanLabel}</TableCell>
                            <TableCell>{row.newPlanLabel}</TableCell>
                            <TableCell align="right">{formatPlanNumber(row.previousDocuments)}</TableCell>
                            <TableCell align="right">{formatPlanNumber(row.newDocuments)}</TableCell>
                            <TableCell>{row.notes || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <Typography variant="body2" color="textSecondary">Aun no hay movimientos de folios registrados para este cliente.</Typography>
                )}
              </Grid>
              </> : null}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => apiOperationsCustomer?.id && refreshApiOperations(apiOperationsCustomer.id)} disabled={apiOperationsLoading || apiOperationsSaving}>
            Actualizar desde APIs
          </Button>
          <Button onClick={() => setApiOperationsOpen(false)} disabled={apiOperationsSaving}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(apiEnvironmentConfirmation)}
        onClose={() => !apiOperationsSaving && setApiEnvironmentConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar cambio de ambientes</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            Va a actualizar ambientes en las APIs reales donde aparece este cliente:
          </Typography>
          <Box className={classes.detailSection}>
            <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
              {apiOperationsCustomer?.name || "Cliente"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              NIT/Cedula: {apiOperationsCustomer?.identificationNumber || "Sin dato"}{apiOperationsCustomer?.dv ? `-${apiOperationsCustomer.dv}` : ""}
            </Typography>
            {apiEnvironmentConfirmation?.changes?.map(change => (
              <Typography key={change.service} variant="body2" color="textSecondary">
                {change.service}: {change.from} a {change.to}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            {apiEnvironmentConfirmation?.detail}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Esta accion quedara registrada en el historial interno con el usuario, cada API consultada y la respuesta recibida.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiEnvironmentConfirmation(null)} disabled={apiOperationsSaving}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={executeSaveApiEnvironments} disabled={apiOperationsSaving}>
            {apiOperationsSaving ? "Aplicando..." : "Confirmar y aplicar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(apiPlanConfirmation)}
        onClose={() => !apiOperationsSaving && setApiPlanConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar asignacion de plan</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            Va a aplicar este cambio en la API real del cliente:
          </Typography>
          <Box className={classes.detailSection}>
            <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
              {apiOperationsCustomer?.name || "Cliente"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Servicio: {apiPlanConfirmation?.serviceLabel || "Sin servicio"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Seleccion: {apiPlanConfirmation?.targetLabel || "Sin seleccion"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Accion: {apiPlanForm.renew ? "Renovar/asignar desde hoy" : "Asignar sin renovar fecha si la API lo permite"}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            {apiPlanConfirmation?.detail || "Confirme solo si desea guardar este cambio en la API del cliente."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiPlanConfirmation(null)} disabled={apiOperationsSaving}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={executeAssignApiPlan} disabled={apiOperationsSaving}>
            {apiOperationsSaving ? "Aplicando..." : "Confirmar y aplicar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={establishmentModalOpen}
        onClose={() => setEstablishmentModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {establishmentForm.id ? "Editar establecimiento" : "Nuevo establecimiento"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre"
                value={establishmentForm.name}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Codigo historico del agente"
                value={establishmentForm.installationCode}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, installationCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={String(establishmentForm.isActive)}
                  onChange={e =>
                    setEstablishmentForm(prev => ({ ...prev, isActive: e.target.value === "true" }))
                  }
                >
                  <MenuItem value="true">Activo</MenuItem>
                  <MenuItem value="false">Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Direccion"
                value={establishmentForm.address}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={departmentOptions}
                value={selectedDepartment}
                onChange={(_event, value) => {
                  setEstablishmentForm(prev => ({
                    ...prev,
                    departmentCode: value ? String(value.code) : "",
                    departmentName: value ? String(value.name) : "",
                    municipalityCode: "",
                    municipalityName: ""
                  }));
                }}
                getOptionLabel={option => option?.name || ""}
                isOptionEqualToValue={(option, value) => String(option.code) === String(value.code)}
                noOptionsText="No hay departamentos disponibles"
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Departamento"
                    placeholder="Seleccione un departamento"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={municipalityOptions}
                value={selectedMunicipality}
                onChange={(_event, value) => {
                  setEstablishmentForm(prev => ({
                    ...prev,
                    municipalityCode: value ? String(value.code) : "",
                    municipalityName: value ? String(value.name) : ""
                  }));
                }}
                getOptionLabel={option => option?.name || ""}
                isOptionEqualToValue={(option, value) => String(option.code) === String(value.code)}
                noOptionsText={establishmentForm.departmentCode ? "No hay municipios disponibles" : "Seleccione primero un departamento"}
                disabled={!establishmentForm.departmentCode}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Municipio"
                    placeholder="Seleccione un municipio"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Telefono principal"
                value={establishmentForm.primaryPhone}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, primaryPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Correo principal"
                value={establishmentForm.primaryEmail}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, primaryEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                label="Notas"
                value={establishmentForm.notes}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEstablishmentModalOpen(false)} disabled={establishmentSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEstablishment}
            color="primary"
            variant="contained"
            disabled={establishmentSaving}
          >
            {establishmentSaving
              ? "Guardando..."
              : establishmentForm.id
                ? "Guardar cambios"
                : "Crear establecimiento"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactLinkDialog}
        onClose={() => {
          setContactLinkDialog(false);
          setContactLinkSelection([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Relacionar contactos al cliente</DialogTitle>
        <DialogContent dividers>
          {contactLinkLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={5}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Autocomplete
              multiple
              fullWidth
              options={contactLinkOptions}
              value={contactLinkSelection}
              onChange={(_event, value) => setContactLinkSelection(value)}
              getOptionLabel={option => buildClientContactLabel(option)}
              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
              filterSelectedOptions
              noOptionsText="No hay contactos disponibles para relacionar"
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Buscar contactos"
                  placeholder="Escriba nombre o numero"
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setContactLinkDialog(false);
              setContactLinkSelection([]);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleManualAssignContacts}
            color="primary"
            variant="contained"
            disabled={contactLinkLoading}
          >
            Relacionar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Clients;

